import { getDevHubUrl, getDocUrl } from '../lib/dev-hub-url.js';
import {
  prepareFormatDate,
  prepareFormatDateOnly,
  prepareFormatDatetime,
  prepareFormatDistanceToNow,
  prepareFormatHours,
} from '../lib/i18n/i18n-date.js';
import { getCountryName } from '../lib/i18n/i18n-display.js';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  prepareNumberBytesFormatter,
  prepareNumberUnitFormatter,
} from '../lib/i18n/i18n-number.js';
import { sanitize } from '../lib/i18n/i18n-sanitize.js';
import { preparePlural } from '../lib/i18n/i18n-string.js';

/**
 * @import { Flavor, FlavorWithMonthlyCost } from '../components/common.types.js'
 */

export const lang = 'en';

const plural = preparePlural(lang);

const formatDate = prepareFormatDate(lang);
const formatDateOnly = prepareFormatDateOnly(lang);
const formatDatetime = prepareFormatDatetime(lang);
const formatHours = prepareFormatHours(lang);
const formatDistanceToNow = prepareFormatDistanceToNow(
  lang,
  (value, unit) => {
    const pluralUnit = plural(value, unit);
    return `${value} ${pluralUnit} ago`;
  },
  'just now',
);

const formatNumberUnit = prepareNumberUnitFormatter(lang);
const BYTES_SI_SEPARATOR = ' ';
const BYTES_SYMBOL = 'B';
const formatBytes = prepareNumberBytesFormatter(lang, BYTES_SYMBOL, BYTES_SI_SEPARATOR);
const formatBytesSi = prepareNumberUnitFormatter(lang, BYTES_SYMBOL, BYTES_SI_SEPARATOR);

/**
 * @param {number} value
 * @return {string}
 */
function getUnit(value) {
  return formatBytesSi(value).split(BYTES_SI_SEPARATOR)[1];
}

// Shared logic between translations, is it a good idea?
/**
 * @param {Flavor} flavor
 * @return {string}
 */
function formatFlavor(flavor) {
  const cpu = `CPUs: ${flavor.cpus}`;
  const shared = flavor.microservice ? ` (shared)` : '';
  const gpu = flavor.gpus > 0 ? `GPUs: ${flavor.gpus}` : '';
  const mem = `RAM: ${formatBytes(flavor.mem * 1024 * 1024)}`;
  return [cpu + shared, gpu, mem].filter((a) => a).join('\n');
}

const getCliInstructions = () =>
  sanitize`To install Clever Tools CLI, follow the instructions from the <cc-link href="${getDocUrl('/cli/install')}" a11y-desc="documentation - Install Clever Tools">documentation</cc-link>.`;

export const translations = {
  //#region cc-addon-admin
  'cc-addon-admin.admin': `Add-on administration`,
  'cc-addon-admin.danger-zone': `Danger zone`,
  'cc-addon-admin.delete': `Delete the add-on`,
  'cc-addon-admin.delete-backups': `Backups are kept according to the retention policy.`,
  'cc-addon-admin.delete-disclaimer': `Deleting this add-on makes it immediately unavailable.`,
  'cc-addon-admin.delete-vm': `The database is kept 24 hours.`,
  'cc-addon-admin.delete-vm-and-backups': `The database is kept 24 hours, backups according to the retention policy.`,
  'cc-addon-admin.delete.dialog.confirm': `Delete the add-on`,
  'cc-addon-admin.delete.dialog.desc': `Be aware that by removing this add-on, all the data related to it may be lost. Please refer to the add-on provider terms of usage for more details.`,
  'cc-addon-admin.delete.dialog.heading': `Confirm deletion`,
  'cc-addon-admin.delete.dialog.label': `Enter the add-on name`,
  'cc-addon-admin.delete.error': /** @param {{ name: string }} _ */ ({ name }) =>
    `Something went wrong while deleting the "${name}" add-on.`,
  'cc-addon-admin.delete.success': /** @param {{ name: string }} _ */ ({ name }) =>
    `The "${name}" add-on has been deleted successfully.`,
  'cc-addon-admin.error-loading': `Something went wrong while loading add-on info.`,
  'cc-addon-admin.heading.name': `Name`,
  'cc-addon-admin.heading.tags': `Tags`,
  'cc-addon-admin.input.name': `Add-on name`,
  'cc-addon-admin.input.tags': `Add-on tags`,
  'cc-addon-admin.tags-description': `Tags allow you to classify your applications and add-ons to create categories`,
  'cc-addon-admin.tags-empty': `No defined tags`,
  'cc-addon-admin.tags-update': `Update tags`,
  'cc-addon-admin.update': `Update name`,
  'cc-addon-admin.update-name.error': `Something went wrong while updating the name of your add-on.`,
  'cc-addon-admin.update-name.success': `The name of your add-on has been updated successfully.`,
  'cc-addon-admin.update-tags.error': `Something went wrong while updating the tags of your add-on.`,
  'cc-addon-admin.update-tags.success': `The tags of your add-on have been updated successfully.`,
  //#endregion
  //#region cc-addon-backups
  'cc-addon-backups.cli.content.download-backup-command': `Download a database backup:`,
  'cc-addon-backups.cli.content.instruction': getCliInstructions,
  'cc-addon-backups.cli.content.intro': `
      You can manage backups directly from your terminal using the commands below.
    `,
  'cc-addon-backups.cli.content.list-backups-command': `List available database backups:`,
  'cc-addon-backups.command-password': `This command will ask for your password, here it is:`,
  'cc-addon-backups.delete': /** @param {{createdAt: string|number}} _ */ ({ createdAt }) =>
    sanitize`Delete the backup from <strong title="${formatDate(createdAt)}">${formatDatetime(createdAt)}</strong>`,
  'cc-addon-backups.delete-command-label': `Delete command`,
  'cc-addon-backups.delete-command-password-label': `Delete command password`,
  'cc-addon-backups.delete.btn': `delete...`,
  'cc-addon-backups.delete.manual.description.es-addon': () =>
    sanitize`You can delete this backup using <cc-link href="https://curl.se/docs/">cURL</cc-link> by executing this command:`,
  'cc-addon-backups.delete.manual.title': `Delete manually`,
  'cc-addon-backups.delete.with-service.description.es-addon': /** @param {{href: string}} _ */ ({ href }) =>
    sanitize`You can delete this backup using Kibana by going to the <cc-link href="${href}">backup repository</cc-link>.`,
  'cc-addon-backups.delete.with-service.title.es-addon': `Delete with Kibana`,
  'cc-addon-backups.description.es-addon': `Backups are managed by Elasticsearch itself. You can define the retention period and backup frequency through Kibana's UI.`,
  'cc-addon-backups.description.es-addon-old': `Backups are managed by Elasticsearch itself. The version of your Elasticsearch does not allow to define a retention policy. Deleting a backup can be done manually through Elasticsearch's API.`,
  'cc-addon-backups.description.jenkins': `Backups are created by archiving Jenkins's data.`,
  'cc-addon-backups.description.mongodb-addon': () =>
    sanitize`Backups are created using the <cc-link href="https://docs.mongodb.com/v4.0/reference/program/mongodump/">mongodump</cc-link> tool.`,
  'cc-addon-backups.description.mysql-addon': () =>
    sanitize`Backups are created using the <cc-link href="https://dev.mysql.com/doc/refman/8.0/en/mysqldump.html">mysqldump</cc-link> tool.`,
  'cc-addon-backups.description.postgresql-addon': () =>
    sanitize`Backups are created using the <cc-link href="https://www.postgresql.org/docs/current/app-pgdump.html">pg_dump</cc-link> tool.`,
  'cc-addon-backups.description.redis-addon': `Backups are created by archiving Redis's data.`,
  'cc-addon-backups.empty': `There are no backups yet.`,
  'cc-addon-backups.link.es-addon': `open in Kibana`,
  'cc-addon-backups.link.es-addon-old': `open in Elasticsearch`,
  'cc-addon-backups.link.jenkins': `download`,
  'cc-addon-backups.link.mongodb-addon': `download`,
  'cc-addon-backups.link.mysql-addon': `download`,
  'cc-addon-backups.link.postgresql-addon': `download`,
  'cc-addon-backups.link.redis-addon': `download`,
  'cc-addon-backups.loading-error': `Something went wrong while loading the backups.`,
  'cc-addon-backups.restore': /** @param {{createdAt: string|number}} _ */ ({ createdAt }) =>
    sanitize`Restore the backup from <strong title="${formatDate(createdAt)}">${formatDatetime(createdAt)}</strong>`,
  'cc-addon-backups.restore-command-label': `Restore command`,
  'cc-addon-backups.restore-command-password-label': `Restore command password`,
  'cc-addon-backups.restore.btn': `restore...`,
  'cc-addon-backups.restore.manual.description.es-addon': () =>
    sanitize`You can restore this backup using <cc-link href="https://curl.se/docs/">cURL</cc-link> by executing this command:`,
  'cc-addon-backups.restore.manual.description.jenkins': `Restoring a Jenkins backup must be done by our support team. Open a support ticket containing the add-on ID and the backup to restore and we will do it for you.`,
  'cc-addon-backups.restore.manual.description.mongodb-addon': () =>
    sanitize`You can restore this backup using the <cc-link href="https://docs.mongodb.com/v4.0/reference/program/mongorestore/">mongorestore</cc-link> tool by executing this command:`,
  'cc-addon-backups.restore.manual.description.mysql-addon': () =>
    sanitize`You can restore this backup using the <cc-link href="https://dev.mysql.com/doc/refman/8.0/en/mysql.html">mysql</cc-link> CLI by executing this command:`,
  'cc-addon-backups.restore.manual.description.postgresql-addon': () =>
    sanitize`You can restore this backup using the <cc-link href="https://www.postgresql.org/docs/current/app-pgrestore.html">pg_restore</cc-link> tool by executing this command:`,
  'cc-addon-backups.restore.manual.description.redis-addon': `Restoring a Redis backup must be done by our support team. Open a support ticket containing the add-on ID and the backup to restore and we will do it for you.`,
  'cc-addon-backups.restore.manual.title': `Restore manually`,
  'cc-addon-backups.restore.with-service.description.es-addon': /** @param {{href: string}} _ */ ({ href }) =>
    sanitize`You can restore this backup using Kibana by going to the <cc-link href="${href}">backup repository</cc-link>.`,
  'cc-addon-backups.restore.with-service.title.es-addon': `Restore with Kibana`,
  'cc-addon-backups.text': /** @param {{createdAt: string|number, expiresAt: string|number}} _ */ ({
    createdAt,
    expiresAt,
  }) => {
    return sanitize`Backup from <strong title="${formatDate(createdAt)}">${formatDatetime(createdAt)}</strong> (expires on <strong>${formatDateOnly(expiresAt)}</strong>)`;
  },
  'cc-addon-backups.text.user-defined-retention': /** @param {{createdAt: string|number}} _ */ ({ createdAt }) =>
    sanitize`Backup from <strong title="${formatDate(createdAt)}">${formatDatetime(createdAt)}</strong> (expires after defined retention)`,
  'cc-addon-backups.title': `Backups`,
  //#endregion
  //#region cc-addon-credentials
  'cc-addon-credentials.choice.admin': `Admin`,
  'cc-addon-credentials.choice.api': `API`,
  'cc-addon-credentials.choice.apm': `APM`,
  'cc-addon-credentials.choice.cli': `CLI`,
  'cc-addon-credentials.choice.default': `Default`,
  'cc-addon-credentials.choice.direct': `Direct`,
  'cc-addon-credentials.choice.elastic': `Elastic`,
  'cc-addon-credentials.choice.kibana': `Kibana`,
  'cc-addon-credentials.doc-link.keycloak': `Secured multi-instances - Documentation`,
  'cc-addon-credentials.doc-link.otoroshi-api': `Manage Otoroshi from its API - Documentation`,
  'cc-addon-credentials.doc-link.otoroshi-ng': `Otoroshi in a Network Group - Documentation`,
  'cc-addon-credentials.error': `Something went wrong while loading information`,
  'cc-addon-credentials.get-credentials.error': `An error occurred while updating the information, please refresh the page`,
  'cc-addon-credentials.heading': `Access`,
  'cc-addon-credentials.ng-multi-instances.disabling.error': `Something went wrong while trying to disable the secured multi-instances`,
  'cc-addon-credentials.ng-multi-instances.disabling.success': `The secured multi-instances have been successfully disabled`,
  'cc-addon-credentials.ng-multi-instances.enabling.error': `Something went wrong while trying to enable the secured multi-instances`,
  'cc-addon-credentials.ng-multi-instances.enabling.success': `The secured multi-instances have been successfully enabled`,
  'cc-addon-credentials.ng-standard.disabling.error': `Something went wrong while trying to disable the Network Group`,
  'cc-addon-credentials.ng-standard.disabling.success': `The Network Group has been successfully disabled`,
  'cc-addon-credentials.ng-standard.enabling.error': `Something went wrong while trying to enable the Network Group`,
  'cc-addon-credentials.ng-standard.enabling.success': `The Network Group has been successfully enabled`,
  'cc-addon-credentials.renew-secret': `Renew secret`,
  'cc-addon-credentials.renew-secret.error': `Something went wrong while trying to renew the secret`,
  'cc-addon-credentials.renew-secret.success': `The secret has been successfully renewed`,
  //#endregion
  //#region cc-addon-credentials-content
  'cc-addon-credentials-content.code.api-client-secret': `API client secret`,
  'cc-addon-credentials-content.code.api-client-user': `API client user`,
  'cc-addon-credentials-content.code.api-key': `API key`,
  'cc-addon-credentials-content.code.api-password': `API password`,
  'cc-addon-credentials-content.code.api-url': `API URL`,
  'cc-addon-credentials-content.code.cluster-full-name': `Cluster full name`,
  'cc-addon-credentials-content.code.database-name': `Database name`,
  'cc-addon-credentials-content.code.direct-host': `Direct host`,
  'cc-addon-credentials-content.code.direct-port': `Direct port`,
  'cc-addon-credentials-content.code.direct-uri': `Direct URI`,
  'cc-addon-credentials-content.code.download-file': `Download File`,
  'cc-addon-credentials-content.code.host': `Host`,
  'cc-addon-credentials-content.code.initial-password': `Initial password`,
  'cc-addon-credentials-content.code.initial-user': `Initial User`,
  'cc-addon-credentials-content.code.key-id': `Key ID`,
  'cc-addon-credentials-content.code.key-secret': `Key Secret`,
  'cc-addon-credentials-content.code.network-group-multi-instances': `Secured multi-instances`,
  'cc-addon-credentials-content.code.network-group-standard': `Network group`,
  'cc-addon-credentials-content.code.open-api-url': `OpenAPI URL`,
  'cc-addon-credentials-content.code.password': `Password`,
  'cc-addon-credentials-content.code.port': `Port`,
  'cc-addon-credentials-content.code.swagger-url': `Swagger UI`,
  'cc-addon-credentials-content.code.tenant-namespace': `Tenant/Namespace`,
  'cc-addon-credentials-content.code.token': `Token`,
  'cc-addon-credentials-content.code.uri': `URI`,
  'cc-addon-credentials-content.code.url': `URL`,
  'cc-addon-credentials-content.code.user': `User`,
  'cc-addon-credentials-content.download-s3cfg-file': `Download a pre-filled s3cfg file`,
  'cc-addon-credentials-content.ng.disable': `Disable`,
  'cc-addon-credentials-content.ng.enable-multi-instances': `Enable secured multi-instances`,
  'cc-addon-credentials-content.ng.enable-standard': `Enable the Network Group`,
  //#endregion
  //#region cc-addon-elasticsearch-options
  'cc-addon-elasticsearch-options.additional-cost': () =>
    sanitize`As such, <strong>enabling these options will result in an increase in credits consumption</strong> as well.`,
  'cc-addon-elasticsearch-options.description': `This add-on is part of the Elastic Stack offer which includes two options. Both these options will be deployed as applications, managed and updated by Clever Cloud on your behalf. They will appear as regular applications that you can stop, scale up or down automatically like one of your own applications.`,
  'cc-addon-elasticsearch-options.details.apm': () =>
    sanitize`Elastic APM server is an application performance monitoring system built on the Elastic Stack. Deploying this will allow you to automatically send APM metrics from any applications linked to the Elasticsearch add-on instance, providing you add the Elastic APM agent to the application code. Learn more in the <cc-link href="https://www.elastic.co/guide/en/apm/get-started/current/overview.html">official APM server documentation</cc-link>.`,
  'cc-addon-elasticsearch-options.details.kibana': () =>
    sanitize`Kibana is the admin UI for the Elastic Stack. It lets you visualize your Elasticsearch data and navigate the stack so you can do anything from tracking query load to understanding the way requests flow through your apps. Learn more in the <cc-link href="https://www.elastic.co/guide/en/kibana/master/index.html">official Kibana documentation</cc-link>.`,
  'cc-addon-elasticsearch-options.error.icon-a11y-name': `Warning`,
  'cc-addon-elasticsearch-options.title': `Options for the Elastic Stack`,
  'cc-addon-elasticsearch-options.warning.apm': `If you enable this option, we'll deploy and manage an Elastic APM server application for you.`,
  'cc-addon-elasticsearch-options.warning.flavor': /** @param {Flavor} flavor */ (flavor) =>
    sanitize`By default, the application will start on a <strong title="${formatFlavor(flavor)}">${flavor.name} instance</strong>.`,
  'cc-addon-elasticsearch-options.warning.kibana': `If you enable this option, we'll deploy and manage a Kibana application for you.`,
  'cc-addon-elasticsearch-options.warning.monthly-cost': /** @param {FlavorWithMonthlyCost['monthlyCost']} _ */ ({
    amount,
    currency,
  }) => sanitize`
      This should cost around <strong>${formatCurrency(lang, amount, { currency })}</strong> per month.`,
  //#endregion
  //#region cc-addon-encryption-at-rest-option
  'cc-addon-encryption-at-rest-option.description': () =>
    sanitize`Encryption at rest encrypts the entire data disk of your add-on. It prevents reading the stored data in case of a physical access to the hard drive. More information in our <cc-link href="${getDocUrl('/administrate/encryption-at-rest')}">documentation</cc-link>.`,
  'cc-addon-encryption-at-rest-option.title': `Encryption at rest`,
  //#endregion
  //#region cc-addon-header
  'cc-addon-header.action.get-config': `Get Kubeconfig`,
  'cc-addon-header.action.open-addon': /** @param {{ linkName: string }} _ */ ({ linkName }) => `Open ${linkName}`,
  'cc-addon-header.action.restart': `Restart`,
  'cc-addon-header.action.restart-rebuild': `Re-build and restart`,
  'cc-addon-header.error': `Something went wrong while loading information`,
  'cc-addon-header.error.fetch-kubeconfig': `An error occurred while refreshing the Kubeconfig link, please refresh the page.`,
  'cc-addon-header.logs.link': `View logs`,
  'cc-addon-header.rebuild.error': `Something went wrong while rebuilding the add-on.`,
  'cc-addon-header.rebuild.success.message': /** @param {{logsUrl: string, docsUrl: string}} _ */ ({
    logsUrl,
    docsUrl,
  }) =>
    sanitize`The process of rebuilding and restarting your add-on and its resources is in progress. See the <cc-link href="${logsUrl}">logs</cc-link> or the <cc-link href=${docsUrl}>documentation</cc-link> for more information.`,
  'cc-addon-header.rebuild.success.title': `Re-build and restart in progress`,
  'cc-addon-header.restart.error': `Something went wrong while restarting the add-on.`,
  'cc-addon-header.restart.success.message': /** @param {{logsUrl: string, docsUrl: string}} _ */ ({
    logsUrl,
    docsUrl,
  }) =>
    sanitize`The process of restarting your add-on and its resources is in progress. See the <cc-link href="${logsUrl}">logs</cc-link> or the <cc-link href=${docsUrl}>documentation</cc-link> for more information.`,
  'cc-addon-header.restart.success.title': `Restart in progress`,
  'cc-addon-header.state-msg.deployment-failed': `Deployment failed`,
  'cc-addon-header.state-msg.deployment-is-active': /** @param {{ providerId: string }} _ */ ({ providerId }) =>
    `${providerId} ${providerId === 'kubernetes' ? 'cluster' : 'instance'} is active`,
  'cc-addon-header.state-msg.deployment-is-deploying': `Deployment in progress`,
  'cc-addon-header.state-msg.unknown-state': `Unknown state, try to restart or contact our support if you have additional questions.`,
  //#endregion
  //#region cc-addon-info
  'cc-addon-info.billing.heading': `Billing`,
  'cc-addon-info.creation-date.heading': `Creation date`,
  'cc-addon-info.creation-date.human-friendly-date': /** @param {{ date: string | number }} _ */ ({ date }) =>
    formatDatetime(date),
  'cc-addon-info.description.heading': `Description`,
  'cc-addon-info.doc-link.cellar': `Cellar - Documentation`,
  'cc-addon-info.doc-link.elastic': `Elastic Stack - Documentation`,
  'cc-addon-info.doc-link.jenkins': `Jenkins - Documentation`,
  'cc-addon-info.doc-link.keycloak': `Keycloak - Documentation`,
  'cc-addon-info.doc-link.kubernetes': `Kubernetes - Documentation`,
  'cc-addon-info.doc-link.materia-kv': `Materia KV - Documentation`,
  'cc-addon-info.doc-link.matomo': `Matomo - Documentation`,
  'cc-addon-info.doc-link.metabase': `Metabase - Documentation`,
  'cc-addon-info.doc-link.otoroshi': `Otoroshi - Documentation`,
  'cc-addon-info.doc-link.pulsar': `Pulsar - Documentation`,
  'cc-addon-info.encryption.heading': `Encryption at rest`,
  'cc-addon-info.error': `Something went wrong while loading information`,
  'cc-addon-info.grafana.link': `Open Grafana`,
  'cc-addon-info.heading': `Information`,
  'cc-addon-info.last-ip.heading': `Last IP`,
  'cc-addon-info.number-of-members.heading': `Number of members`,
  'cc-addon-info.number-of-peers.heading': `Number of peers`,
  'cc-addon-info.plan.heading': `Plan`,
  'cc-addon-info.resources.heading': `Resources`,
  'cc-addon-info.role.heading': `Role`,
  'cc-addon-info.scalability-link.heading': `Scalability`,
  'cc-addon-info.scalability.link': `Configure scalability`,
  'cc-addon-info.service.name.addon': /** @param {{name: string}} _ */ ({ name }) => `${name} add-on`,
  'cc-addon-info.service.name.app': /** @param {{name: string}} _ */ ({ name }) => `${name} application`,
  'cc-addon-info.specification.connection-limit': `Connection limit`,
  'cc-addon-info.specification.cpu': `vCPUs`,
  'cc-addon-info.specification.data-exploration': `Data Exploration`,
  'cc-addon-info.specification.databases': `Databases`,
  'cc-addon-info.specification.db-analysis': `DB Analysis`,
  'cc-addon-info.specification.dedicated': `Dedicated`,
  'cc-addon-info.specification.disk-size': `Disk size`,
  'cc-addon-info.specification.gpu': `GPUs`,
  'cc-addon-info.specification.has-logs': `Logs`,
  'cc-addon-info.specification.has-metrics': `Metrics`,
  'cc-addon-info.specification.heading': `Specifications`,
  'cc-addon-info.specification.is-migratable': `Migration tool`,
  'cc-addon-info.specification.max-db-size': `Max DB size`,
  'cc-addon-info.specification.memory': `RAM`,
  'cc-addon-info.specification.plan': `Plan`,
  'cc-addon-info.specification.users': `Users`,
  'cc-addon-info.specification.version': `Version`,
  'cc-addon-info.subnet.heading': `Subnet`,
  'cc-addon-info.total-content.buckets': `Buckets`,
  'cc-addon-info.total-content.heading': `Content`,
  'cc-addon-info.total-content.objects': `Objects`,
  'cc-addon-info.traffic.heading': `Traffic`,
  'cc-addon-info.traffic.inbound': `Inbound`,
  'cc-addon-info.traffic.inbound.inbound-in-bytes': /** @param {{bytes: number}} _ */ ({ bytes }) =>
    formatBytes(bytes, 1),
  'cc-addon-info.traffic.outbound': `Outbound`,
  'cc-addon-info.traffic.outbound.outbound-in-bytes': /** @param {{bytes: number}} _ */ ({ bytes }) =>
    formatBytes(bytes, 1),
  'cc-addon-info.type.boolean': /** @param {{boolean: boolean}} _ */ ({ boolean }) => `${boolean ? 'Yes' : 'No'}`,
  'cc-addon-info.type.boolean-shared': /** @param {{shared: boolean}} _ */ ({ shared }) =>
    `${shared ? 'Shared' : 'Dedicated'}`,
  'cc-addon-info.type.bytes': /** @param {{bytes: number}} _ */ ({ bytes }) => formatBytes(bytes, 0, 3),
  'cc-addon-info.type.number': /** @param {{number: number}} _ */ ({ number }) => formatNumber(lang, number),
  'cc-addon-info.type.number-cpu-runtime': /** @param {{cpu: number, shared: boolean}} _ */ ({ cpu, shared }) => {
    return shared
      ? sanitize`<em title="Lower priority access to vCPU">${formatNumber(lang, cpu)}<code>*</code></em>`
      : formatNumber(lang, cpu);
  },
  'cc-addon-info.used-spaces.heading': `Used storage`,
  'cc-addon-info.used-spaces.size': `Size`,
  'cc-addon-info.used-spaces.size.size-in-bytes': /** @param {{bytes: number}} _ */ ({ bytes }) =>
    formatBytes(bytes, 1),
  'cc-addon-info.version.btn': `Update available`,
  'cc-addon-info.version.dialog.btn.submit': `Update and rebuild`,
  'cc-addon-info.version.dialog.desc': /** @param {{ url: string }} _ */ ({ url }) =>
    sanitize`One or more new versions are available. Choose the version you wish to apply and confirm to start and rebuild the application. Learn more in our <cc-link href="${url}">Changelog</cc-link>.`,
  'cc-addon-info.version.dialog.heading': `Update available`,
  'cc-addon-info.version.dialog.select.desc': `Version from`,
  'cc-addon-info.version.dialog.select.help': /** @param {{ currentVersion: string }} _ */ ({ currentVersion }) =>
    `Select the target version to update from ${currentVersion}`,
  'cc-addon-info.version.dialog.select.label': () => sanitize`<strong>to</strong>`,
  'cc-addon-info.version.heading': `Version`,
  'cc-addon-info.version.update.error': `Something went wrong while updating the version`,
  'cc-addon-info.version.update.success.content': /** @param {{logsUrl: string}} _*/ ({ logsUrl }) =>
    sanitize`An update and rebuild phase is in progress. See the application live <cc-link href="${logsUrl}">logs</cc-link>.`,
  'cc-addon-info.version.update.success.heading': /** @param {{ version: string }} _ */ ({ version }) =>
    `Update to ${version} in progress`,
  //#endregion
  //#region cc-addon-jenkins-options
  'cc-addon-jenkins-options.description': `Choose the options you want for your Jenkins add-on.`,
  'cc-addon-jenkins-options.title': `Options for the Jenkins add-on`,
  //#endregion
  //#region cc-addon-linked-apps
  'cc-addon-linked-apps.details': `Here's the list of applications linked to this add-on. The add-on exposes its environment variables to those linked applications.`,
  'cc-addon-linked-apps.loading-error': `Something went wrong while loading linked applications.`,
  'cc-addon-linked-apps.no-linked-applications': `No applications linked yet.`,
  'cc-addon-linked-apps.title': `Linked applications`,
  //#endregion
  //#region cc-addon-mongodb-options
  'cc-addon-mongodb-options.description': `Choose the options you want for your MongoDB add-on.`,
  'cc-addon-mongodb-options.title': `Options for the MongoDB add-on`,
  //#endregion
  //#region cc-addon-mysql-options
  'cc-addon-mysql-options.description': `Choose the options you want for your MySQL add-on.`,
  'cc-addon-mysql-options.title': `Options for the MySQL add-on`,
  //#endregion
  //#region cc-addon-option
  'cc-addon-option.disabled': `Disabled`,
  'cc-addon-option.enabled': `Enabled`,
  //#endregion
  //#region cc-addon-option-form
  'cc-addon-option-form.confirm': `Confirm options`,
  //#endregion
  //#region cc-addon-postgresql-options
  'cc-addon-postgresql-options.description': `Choose the options you want for your PostgreSQL add-on.`,
  'cc-addon-postgresql-options.title': `Options for the PostgreSQL add-on`,
  //#endregion
  //#region cc-addon-redis-options
  'cc-addon-redis-options.description': `Choose the options you want for your Redis add-on.`,
  'cc-addon-redis-options.title': `Options for the Redis add-on`,
  //#endregion
  //#region cc-ansi-palette
  'cc-ansi-palette.compliant': `Color compliant with RGAA`,
  'cc-ansi-palette.fg-bg': /** @param {{foreground: string, background: string}} _ */ ({ foreground, background }) =>
    `Foreground: ${foreground}, Background: ${background}`,
  'cc-ansi-palette.hover': /** @param {{color: string}} _ */ ({ color }) => `Hover background: ${color}`,
  'cc-ansi-palette.not-compliant': `Color not compliant with RGAA`,
  'cc-ansi-palette.ratio': /** @param {{ratio: number}} _ */ ({ ratio }) =>
    formatNumber(lang, ratio, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(5, '0'),
  'cc-ansi-palette.selected': /** @param {{color: string}} _ */ ({ color }) => `Selected background: ${color}`,
  //#endregion
  //#region cc-article-card
  'cc-article-card.date': /** @param {{date: string}} _ */ ({ date }) => formatDateOnly(date),
  //#endregion
  //#region cc-article-list
  'cc-article-list.error': `An error occurred while loading articles.`,
  //#endregion
  //#region cc-beta
  'cc-beta.label': `beta`,
  //#endregion
  //#region cc-block-details
  'cc-block-details.cli.text': `Command line`,
  //#endregion
  //#region cc-button
  'cc-button.cancel': `Click to cancel`,
  //#endregion
  //#region cc-cellar-bucket-list
  'cc-cellar-bucket-list.count': /** @param {{count: number}} _ */ ({ count }) => formatNumber(lang, count),
  'cc-cellar-bucket-list.create.bucket-name.help.case': `Bucket names must not contain uppercase characters or underscores.`,
  'cc-cellar-bucket-list.create.bucket-name.help.ip': `Bucket names cannot be formatted as an IP address.`,
  'cc-cellar-bucket-list.create.bucket-name.help.labels': `Bucket names must be a series of one or more labels. Adjacent labels are separated by a single period (.). Bucket names can contain lowercase letters, numbers, and hyphens. Each label must start and end with a lowercase letter or a number.`,
  'cc-cellar-bucket-list.create.bucket-name.help.size': `Bucket names must be between 3 and 63 characters long.`,
  'cc-cellar-bucket-list.create.bucket-name.help.start': `Bucket names must start with a lowercase letter or number.`,
  'cc-cellar-bucket-list.create.bucket-name.label': `Bucket name`,
  'cc-cellar-bucket-list.create.error.bucket-already-exists': `This bucket name is already taken`,
  'cc-cellar-bucket-list.create.error.bucket-name-invalid': `The bucket name is invalid`,
  'cc-cellar-bucket-list.create.error.too-many-buckets': `You reached the maximum number of buckets. You can't create more.`,
  'cc-cellar-bucket-list.create.submit': `Create bucket`,
  'cc-cellar-bucket-list.create.title': `Create bucket`,
  'cc-cellar-bucket-list.date': /** @param {{date: string}} _ */ ({ date }) => formatDateOnly(date),
  'cc-cellar-bucket-list.details.actions.delete.button': `Delete the bucket`,
  'cc-cellar-bucket-list.details.actions.delete.must-be-empty': `Empty the bucket before deleting it`,
  'cc-cellar-bucket-list.details.actions.title': `Actions`,
  'cc-cellar-bucket-list.details.heading': `Bucket details`,
  'cc-cellar-bucket-list.details.overview.created-at': `Creation date`,
  'cc-cellar-bucket-list.details.overview.date': /** @param {{date: string}} _ */ ({ date }) => formatDate(date),
  'cc-cellar-bucket-list.details.overview.objects-count': `Number of objects`,
  'cc-cellar-bucket-list.details.overview.size': `Size`,
  'cc-cellar-bucket-list.details.overview.size-in-bytes': /** @param {{size: number}} _ */ ({ size }) => {
    const formatted = formatBytes(size);
    const exact = `${formatNumber(lang, size)}${BYTES_SI_SEPARATOR}${BYTES_SYMBOL}`;
    return formatted === exact
      ? formatted
      : `${formatted} (${formatNumber(lang, size)}${BYTES_SI_SEPARATOR}byte${size <= 1 ? '' : 's'})`;
  },
  'cc-cellar-bucket-list.details.overview.title': `Bucket overview`,
  'cc-cellar-bucket-list.details.overview.updated-at': `Last update`,
  'cc-cellar-bucket-list.details.overview.versioning': `Versioning`,
  'cc-cellar-bucket-list.details.overview.versioning.disabled': `Disabled`,
  'cc-cellar-bucket-list.details.overview.versioning.enabled': `Enabled`,
  'cc-cellar-bucket-list.details.overview.versioning.suspended': `Suspended`,
  'cc-cellar-bucket-list.empty.no-filtered-items': `No buckets matching the filter were found`,
  'cc-cellar-bucket-list.empty.no-items': `You don't have any buckets`,
  'cc-cellar-bucket-list.error': `Something went wrong while loading the list of buckets`,
  'cc-cellar-bucket-list.error.bucket-creation-failed': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Failed to create bucket ${bucketName}`,
  'cc-cellar-bucket-list.error.bucket-deletion-failed': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Failed to delete bucket ${bucketName}`,
  'cc-cellar-bucket-list.error.bucket-fetch-failed': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Failed to get bucket ${bucketName}`,
  'cc-cellar-bucket-list.error.bucket-not-empty': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Empty bucket ${bucketName} before deleting it`,
  'cc-cellar-bucket-list.error.bucket-not-found': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Bucket ${bucketName} does not exist`,
  'cc-cellar-bucket-list.grid.a11y-name': `List of buckets`,
  'cc-cellar-bucket-list.grid.column.last-update': `Last update`,
  'cc-cellar-bucket-list.grid.column.name': `Bucket name`,
  'cc-cellar-bucket-list.grid.column.objects': `Objects`,
  'cc-cellar-bucket-list.grid.column.size': `Size`,
  'cc-cellar-bucket-list.grid.show-details.a11y-name': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Show details for bucket ${bucketName}`,
  'cc-cellar-bucket-list.heading.create.button': `Create bucket`,
  'cc-cellar-bucket-list.heading.filter.button': `Filter`,
  'cc-cellar-bucket-list.heading.filter.label': `Filter`,
  'cc-cellar-bucket-list.heading.title': `List of buckets`,
  'cc-cellar-bucket-list.size': /** @param {{size: number}} _ */ ({ size }) => formatBytes(size),
  'cc-cellar-bucket-list.success.bucket-already-deleted': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Bucket ${bucketName} was already deleted`,
  'cc-cellar-bucket-list.success.bucket-created': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Bucket ${bucketName} created successfully`,
  'cc-cellar-bucket-list.success.bucket-deleted': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Bucket ${bucketName} deleted successfully`,
  //#endregion
  //#region cc-cellar-explorer
  'cc-cellar-explorer.error': `Something went wrong while loading the explorer. Refresh the page or try again later.`,
  //#endregion
  //#region cc-cellar-object-list
  'cc-cellar-object-list.add-directory.dialog.heading': `Add a new directory`,
  'cc-cellar-object-list.add-directory.dialog.label': `Directory name`,
  'cc-cellar-object-list.add-directory.dialog.notice': `Upload an object after creating a directory, otherwise the directory will not be persisted in your bucket.`,
  'cc-cellar-object-list.add-directory.dialog.submit': `Create directory`,
  'cc-cellar-object-list.back-to-bucket-list': `Back to bucket list`,
  'cc-cellar-object-list.badge.new': `New`,
  'cc-cellar-object-list.button.upload': `Upload object`,
  'cc-cellar-object-list.create.directory-name.help': `Directory name cannot contain non-printable characters (128–255 decimal characters) or the following characters: / \\ { } ^ % \` [ ] " < > ~ # |`,
  'cc-cellar-object-list.date': /** @param {{date: string}} _ */ ({ date }) => formatDateOnly(date),
  'cc-cellar-object-list.details.actions.delete.button': `Delete object`,
  'cc-cellar-object-list.details.actions.download.button': `Download object`,
  'cc-cellar-object-list.details.actions.title': `Actions`,
  'cc-cellar-object-list.details.heading': `Object details`,
  'cc-cellar-object-list.details.overview.content-type': `Content type`,
  'cc-cellar-object-list.details.overview.date': /** @param {{date: string}} _ */ ({ date }) => formatDate(date),
  'cc-cellar-object-list.details.overview.location': `Location`,
  'cc-cellar-object-list.details.overview.size': `Size`,
  'cc-cellar-object-list.details.overview.size-in-bytes': /** @param {{size: number}} _ */ ({ size }) => {
    const formatted = formatBytes(size);
    const exact = `${formatNumber(lang, size)}${BYTES_SI_SEPARATOR}${BYTES_SYMBOL}`;
    return formatted === exact
      ? formatted
      : `${formatted} (${formatNumber(lang, size)}${BYTES_SI_SEPARATOR}byte${size <= 1 ? '' : 's'})`;
  },
  'cc-cellar-object-list.details.overview.title': `Object overview`,
  'cc-cellar-object-list.details.overview.updated-at': `Last update`,
  'cc-cellar-object-list.empty.no-filtered-items': `No objects matching the filter were found`,
  'cc-cellar-object-list.empty.no-items': `This bucket is empty`,
  'cc-cellar-object-list.error': `Something went wrong while loading the list of objects`,
  'cc-cellar-object-list.error.bucket-not-found': /** @param {{bucketName: string}} _ */ ({ bucketName }) =>
    `Bucket ${bucketName} does not exist`,
  'cc-cellar-object-list.error.directory-already-exists': /** @param {{directoryName: string}} _ */ ({
    directoryName,
  }) => `Directory ${directoryName} already exists`,
  'cc-cellar-object-list.error.directory-name-invalid': `The directory name contains an invalid character (see the help message above)`,
  'cc-cellar-object-list.error.object-deletion-failed': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Failed to delete object ${objectKey}`,
  'cc-cellar-object-list.error.object-download-failed': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Failed to download object ${objectKey}`,
  'cc-cellar-object-list.error.object-fetch-failed': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Failed to get object ${objectKey}`,
  'cc-cellar-object-list.error.object-not-found': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Object ${objectKey} does not exist`,
  'cc-cellar-object-list.error.object-upload-failed': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Failed to upload object ${objectKey}`,
  'cc-cellar-object-list.grid.a11y-name': `List of objects`,
  'cc-cellar-object-list.grid.column.last-update': `Last update`,
  'cc-cellar-object-list.grid.column.name': `Name`,
  'cc-cellar-object-list.grid.column.size': `Size`,
  'cc-cellar-object-list.grid.show-details.a11y-name': /** @param {{objectName: string}} _ */ ({ objectName }) =>
    `Show details for object ${objectName}`,
  'cc-cellar-object-list.heading.add.directory': `New directory`,
  'cc-cellar-object-list.heading.filter.button': `Filter`,
  'cc-cellar-object-list.heading.filter.label': `Filter`,
  'cc-cellar-object-list.heading.title': `List of objects`,
  'cc-cellar-object-list.icon.icon-file': `File`,
  'cc-cellar-object-list.icon.icon-file-archive': `Archive`,
  'cc-cellar-object-list.icon.icon-file-audio': `Audio file`,
  'cc-cellar-object-list.icon.icon-file-image': `Image file`,
  'cc-cellar-object-list.icon.icon-file-pdf': `PDF file`,
  'cc-cellar-object-list.icon.icon-file-text': `Text file`,
  'cc-cellar-object-list.icon.icon-file-video': `Video file`,
  'cc-cellar-object-list.nav-label': `Breadcrumb: Object list`,
  'cc-cellar-object-list.page.next': `Next page`,
  'cc-cellar-object-list.page.previous': `Previous page`,
  'cc-cellar-object-list.size': /** @param {{size: number}} _ */ ({ size }) => formatBytes(size),
  'cc-cellar-object-list.success.directory-created': /** @param {{directoryName: string}} _ */ ({ directoryName }) =>
    `Directory "${directoryName}" created successfully`,
  'cc-cellar-object-list.success.object-already-deleted': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Object ${objectKey} was already deleted`,
  'cc-cellar-object-list.success.object-deleted': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Object ${objectKey} deleted successfully`,
  'cc-cellar-object-list.success.object-uploaded': /** @param {{objectKey: string}} _ */ ({ objectKey }) =>
    `Object ${objectKey} uploaded successfully`,
  //#endregion
  //#region cc-clipboard
  'cc-clipboard.copied': `The text has been copied`,
  'cc-clipboard.copy': /** @param {{text: string}} _ */ ({ text }) =>
    `Copy to clipboard - ${text.substring(0, 5).trim()}${text.length >= 5 ? '…' : ''}`,
  'cc-clipboard.copy.empty': `Copy to clipboard`,
  //#endregion
  //#region cc-datetime-relative
  'cc-datetime-relative.distance': /** @param {{date: string|number}} _ */ ({ date }) => formatDistanceToNow(date),
  'cc-datetime-relative.title': /** @param {{date: string|number}} _ */ ({ date }) => formatDate(date),
  //#endregion
  //#region cc-dialog
  'cc-dialog.close': `Close`,
  //#endregion
  //#region cc-dialog-confirm-actions
  'cc-dialog-confirm-actions.cancel': `Cancel`,
  //#endregion
  //#region cc-dialog-confirm-form
  'cc-dialog-confirm-form.error': /** @param {{ name: string }} _ */ ({ name }) =>
    `Invalid value. Enter "${name}" as value`,
  //#endregion
  //#region cc-doc-card
  'cc-doc-card.link': /** @param {{link: string, product: string}} _ */ ({ link, product }) =>
    sanitize`<cc-link href=${link} a11y-desc="Read the documentation - ${product}">Read the documentation</cc-link>`,
  'cc-doc-card.skeleton-link-title': `Read the documentation`,
  //#endregion
  //#region cc-doc-list
  'cc-doc-list.error': `An error occurred while loading documentation`,
  //#endregion
  //#region cc-domain-management
  'cc-domain-management.certif.automated': () =>
    sanitize`Whether you use <code>cleverapps.io</code> or your own domain names with applications hosted by Clever Cloud, a Let's Encrypt certificate is automatically issued and renewed for HTTPS/TLS access. No action is required from you, this is all automated. For specific cases, refer to <cc-link href="${getDocUrl('/administrate/ssl')}">Installing TLS Certificates</cc-link>.`,
  'cc-domain-management.certif.custom': () =>
    sanitize`You can provide your own certificate by using the <cc-link href="https://api.clever-cloud.com/v2/certificates/new">Clever Cloud Certificate Manager</cc-link>.`,
  'cc-domain-management.certif.heading': `Secure your application`,
  'cc-domain-management.create-dialog.confirm-button': `Add domain`,
  'cc-domain-management.create-dialog.desc': /** @param {{ domainWithPathPrefix: string }} _ */ ({
    domainWithPathPrefix,
  }) =>
    sanitize`The subdomain <code>${domainWithPathPrefix}</code> will be created without SSL certificate, allowing HTTP access only.`,
  'cc-domain-management.create-dialog.heading': `Create HTTP only subdomain`,
  'cc-domain-management.create-dialog.warning': () =>
    sanitize`Only direct domains of <code>cleverapps.io</code> are secured with SSL certificates, not <code>xx.yy.cleverapps.io</code> domains.`,
  'cc-domain-management.delete-dialog.confirm-button': `Delete`,
  'cc-domain-management.delete-dialog.desc': `By deleting this domain name, your application will immediatly become unreachable from that specific domain name.`,
  'cc-domain-management.delete-dialog.heading': `Remove the domain name`,
  'cc-domain-management.delete-dialog.input-label': `Enter the domain name to confirm its deletion`,
  'cc-domain-management.dns.a.desc': () =>
    sanitize`<p>If you choose to use <code>A</code> records, for instance with a root domain (APEX), you'll need to update them yourself. Follow our <cc-link href="${getDevHubUrl('/changelog')}">changelog</cc-link> or check our <cc-link href="${getDevHubUrl('/api/v4/#load-balancers')}">v4 API documentation</cc-link> for this.</p>`,
  'cc-domain-management.dns.a.heading': `A records`,
  'cc-domain-management.dns.a.label': `A Record values`,
  'cc-domain-management.dns.cli.content.diag-conf-command': `Diagnose the current configuration:`,
  'cc-domain-management.dns.cli.content.instruction': getCliInstructions,
  'cc-domain-management.dns.cname.desc': () =>
    sanitize`<p>Using a <code>CNAME</code> record is recommended. This keeps your configuration up to date.</p>`,
  'cc-domain-management.dns.cname.heading': `CNAME record`,
  'cc-domain-management.dns.cname.label': `CNAME record value`,
  'cc-domain-management.dns.desc':
    () => sanitize`<p>To associate a domain managed by a third-party provider to your Clever Cloud application, you need to configure its DNS zone.</p>
<p>This may be achieved using a <code>CNAME</code> or <code>A</code> records. We recommend using a <code>CNAME</code> record, as you won't have to reconfigure it if we change our IPs.</p>`,
  'cc-domain-management.dns.documentation.text': `DNS records - Documentation`,
  'cc-domain-management.dns.heading': `Configure your DNS`,
  'cc-domain-management.dns.info.desc': () =>
    sanitize`If you are using a dedicated load balancer, refer to its configuration or <cc-link href="/ticket-center-choice">contact support</cc-link>. Our team can also help you to order such a service. For a domain with no subdomains (APEX) or a subdomain with its own DNS zone, refer to our <cc-link href="${getDocUrl('/administrate/domain-names')}">DNS & Domains documentation</cc-link>.`,
  'cc-domain-management.dns.info.heading': `Dedicated load balancers & specific cases`,
  'cc-domain-management.dns.loading-error': `Something went wrong while loading DNS information`,
  'cc-domain-management.form.domain.error.contains-path': /** @param {{path: string}} _ */ ({ path }) =>
    `Enter the "${path}" part within the "Route" input field`,
  'cc-domain-management.form.domain.error.empty': `Enter a domain`,
  'cc-domain-management.form.domain.error.format': `Enter a valid domain, for instance "example.com"`,
  'cc-domain-management.form.domain.error.wildcard': () =>
    sanitize`Enter a valid domain.<br>Wildcards "*" may only be used as subdomains like: "*.example.com"`,
  'cc-domain-management.form.domain.help': () =>
    sanitize`For instance: <code>example.com</code>, <code>*.example.com</code> or <code>example.cleverapps.io</code>`,
  'cc-domain-management.form.domain.label': `Domain name`,
  'cc-domain-management.form.info.cleverapps': () =>
    sanitize`By default, an application is automatically associated to <code>app_id.cleverapps.io</code> as primary domain. You can remove it or change the subdomain freely, but <code>xxx.cleverapps.io</code> should only be used for testing purposes (see our <cc-link href="${getDocUrl('/administrate/domain-names/#testing-with-cleverappsio-domain')}">documentation</cc-link>).`,
  'cc-domain-management.form.info.docs': `You can associate one or more domain names with your application. The primary domain is the one that will be used in Console links and in e-mails sent to you. Several applications can share the same domain, each with a specific subdomain and/or route.`,
  'cc-domain-management.form.path.help': () => sanitize`For example: <code>/api</code> or <code>/blog</code>`,
  'cc-domain-management.form.path.label': `Route`,
  'cc-domain-management.form.submit': `Add a domain`,
  'cc-domain-management.form.submit.error': /** @param {{domain: string}} _ */ ({ domain }) =>
    `An error occurred while trying to add "${domain}"`,
  'cc-domain-management.form.submit.error-duplicate.heading': `Domain name not available`,
  'cc-domain-management.form.submit.error-duplicate.text': /** @param {{domain: string}} _ */ ({ domain }) =>
    sanitize`<p>"${domain}" is already associated to another application within Clever Cloud.</p><p>For more information, contact our support team.</p>`,
  'cc-domain-management.form.submit.success': /** @param {{domain: string}} _ */ ({ domain }) =>
    `"${domain}" has been successfully associated to your application`,
  'cc-domain-management.form.submit.success-config': /** @param {{domain: string}} _ */ ({ domain }) =>
    sanitize`<p>"${domain}" has been successfully associated to your application.</p><p>A manual configuration is required to make the domain point to Clever Cloud. Refer to the <strong>Configure your DNS</strong> section for more information</p>`,
  'cc-domain-management.list.badge.http-only.alt': `Warning`,
  'cc-domain-management.list.badge.http-only.text': `HTTP only`,
  'cc-domain-management.list.badge.primary': `Primary`,
  'cc-domain-management.list.badge.testing-only': `Testing purposes only`,
  'cc-domain-management.list.btn.delete.a11y-name': /** @param {{domain: string}} _ */ ({ domain }) =>
    `Delete domain - ${domain}`,
  'cc-domain-management.list.btn.delete.text': `Delete`,
  'cc-domain-management.list.btn.primary.a11y-name': /** @param {{domain: string}} _ */ ({ domain }) =>
    `Mark as primary domain - ${domain}`,
  'cc-domain-management.list.btn.primary.text': `Mark as primary`,
  'cc-domain-management.list.delete.error': /** @param {{domain: string}} _ */ ({ domain }) =>
    `Something went wrong while trying to delete the "${domain}" domain`,
  'cc-domain-management.list.delete.success': /** @param {{domain: string}} _ */ ({ domain }) =>
    `"${domain}" has been deleted successfully`,
  'cc-domain-management.list.empty': `No domain associated to this application`,
  'cc-domain-management.list.error-not-found.heading': `Domain not found`,
  'cc-domain-management.list.error-not-found.text': /** @param {{domain: string}} _ */ ({ domain }) =>
    sanitize`<p>"${domain}" may have been removed by someone else after loading the list of domains.<p><p><strong>Refresh your page</strong> to get the updated list of domains.</p>`,
  'cc-domain-management.list.heading': `Domain names linked to this application`,
  'cc-domain-management.list.http-only.notice': () =>
    sanitize`Only direct domains of <code>cleverapps.io</code> are secured with SSL certificates, not <code>xx.yy.cleverapps.io</code> domains.`,
  'cc-domain-management.list.link.title': /** @param {{domainUrl: string}} _ */ ({ domainUrl }) =>
    `Open - ${domainUrl} - new window`,
  'cc-domain-management.list.loading-error': `Something went wrong while loading domains associated to this application`,
  'cc-domain-management.list.primary.error': /** @param {{domain: string}} _ */ ({ domain }) =>
    `Something went wrong while trying to mark "${domain}" as primary domain`,
  'cc-domain-management.list.primary.success': /** @param {{domain: string}} _ */ ({ domain }) =>
    `"${domain}" has been successfully marked as primary domain`,
  'cc-domain-management.main-heading': `Manage your domain names`,
  'cc-domain-management.names.cli.content.add-domain-command': `Add a domain:`,
  'cc-domain-management.names.cli.content.diag-dns-records-command': `Diagnose DNS records:`,
  'cc-domain-management.names.cli.content.instruction': getCliInstructions,
  'cc-domain-management.names.cli.content.intro': `
      You can manage domains directly from your terminal using the commands below.
    `,
  'cc-domain-management.names.cli.content.list-command': `List domains:`,
  'cc-domain-management.names.documentation.text': `Domain names - Documentation`,
  'cc-domain-management.new-window': `New Window`,
  'cc-domain-management.tls.certificates.documentation.text': `TLS certificates - Documentation`,
  //#endregion
  //#region cc-drawer
  'cc-drawer.close': `Close`,
  //#endregion
  //#region cc-elasticsearch-info
  'cc-elasticsearch-info.documentation.text': `Elasticsearch - Documentation`,
  'cc-elasticsearch-info.error': `Something went wrong while loading add-ons linked to this application.`,
  'cc-elasticsearch-info.info': `Info`,
  'cc-elasticsearch-info.link.apm': `Open APM`,
  'cc-elasticsearch-info.link.elasticsearch': `Check out the Elasticsearch add-on`,
  'cc-elasticsearch-info.link.kibana': `Open Kibana`,
  'cc-elasticsearch-info.text': `This add-on is part of the Elastic Stack offer. You can find the documentation and linked services right below.`,
  //#endregion
  //#region cc-email-list
  'cc-email-list.loading.error': `Something went wrong while loading email addresses.`,
  'cc-email-list.primary.action.resend-confirmation-email': `Resend a confirmation email`,
  'cc-email-list.primary.action.resend-confirmation-email.error': /** @param {{address: string}} _ */ ({ address }) =>
    sanitize`Something went wrong while sending the confirmation email to <strong>${address}</strong>.`,
  'cc-email-list.primary.action.resend-confirmation-email.success.message': /** @param {{address: string}} _ */ ({
    address,
  }) =>
    sanitize`To complete the process, you must click on the link that has been sent to you by email at <strong>${address}</strong>.`,
  'cc-email-list.primary.action.resend-confirmation-email.success.title': `A confirmation email has been sent`,
  'cc-email-list.primary.description': `This is the address used to create your account. All notifications are sent to this address.`,
  'cc-email-list.primary.email.unverified': `Not verified`,
  'cc-email-list.primary.email.verified': `Verified`,
  'cc-email-list.primary.title': `Primary email address`,
  'cc-email-list.secondary.action.add': `Add email address`,
  'cc-email-list.secondary.action.add.error': /** @param {{address: string}} _ */ ({ address }) =>
    sanitize`Something went wrong while adding the secondary email address <strong>${address}</strong>.`,
  'cc-email-list.secondary.action.add.success.message': /** @param {{address: string}} _ */ ({ address }) =>
    sanitize`To complete the process you must confirm your registration by clicking on the link that was sent to you by email at <strong>${address}</strong>.`,
  'cc-email-list.secondary.action.add.success.title': `Secondary email address addition has been taken into account`,
  'cc-email-list.secondary.action.delete.accessible-name': /** @param {{address: string}} _ */ ({ address }) =>
    `Delete - ${address}`,
  'cc-email-list.secondary.action.delete.error': /** @param {{address: string}} _ */ ({ address }) =>
    sanitize`Something went wrong while deleting secondary email address <strong>${address}</strong>.`,
  'cc-email-list.secondary.action.delete.name': `Delete`,
  'cc-email-list.secondary.action.delete.success': /** @param {{address: string}} _ */ ({ address }) =>
    sanitize`Secondary email address <strong>${address}</strong> deleted successfully.`,
  'cc-email-list.secondary.action.mark-as-primary.accessible-name': /** @param {{address: string}} _ */ ({ address }) =>
    `Mark as primary - ${address}`,
  'cc-email-list.secondary.action.mark-as-primary.error': /** @param {{address: string}} _ */ ({ address }) =>
    sanitize`Something went wrong while marking secondary email address <strong>${address}</strong> as primary.`,
  'cc-email-list.secondary.action.mark-as-primary.name': `Mark as primary`,
  'cc-email-list.secondary.action.mark-as-primary.success': /** @param {{address: string}} _ */ ({ address }) =>
    sanitize`Secondary email address <strong>${address}</strong> has been successfully marked as primary.`,
  'cc-email-list.secondary.address-input.error.already-defined': `This email address already belongs to you`,
  'cc-email-list.secondary.address-input.error.used': `This email address does not belong to you`,
  'cc-email-list.secondary.address-input.format': `name@example.com`,
  'cc-email-list.secondary.address-input.label': `Email address`,
  'cc-email-list.secondary.description': `Unlike the primary email address, no notifications are sent to these addresses. You can be invited inside an organisation with any of your secondary email addresses.`,
  'cc-email-list.secondary.title': `Secondary email addresses`,
  'cc-email-list.title': `Email addresses`,
  //#endregion
  //#region cc-env-var-create
  'cc-env-var-create.create-button': `Add`,
  'cc-env-var-create.errors.already-defined-name': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Name <code>${name}</code> is already defined`,
  'cc-env-var-create.errors.invalid-name': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Name <code>${name}</code> is invalid`,
  'cc-env-var-create.info.java-prop': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Variable <code>${name}</code> will only be injected as a Java property and won't be part of the environment, <cc-link href="${getDocUrl('/develop/env-variables/#environment-variables-rules-and-formats')}">more details</cc-link>`,
  'cc-env-var-create.name.label': `Variable name`,
  'cc-env-var-create.value.label': `Variable value`,
  //#endregion
  //#region cc-env-var-editor-expert
  'cc-env-var-editor-expert.errors.duplicated-name': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`be careful, the name <code>${name}</code> is already defined`,
  'cc-env-var-editor-expert.errors.invalid-line': () =>
    sanitize`this line is not valid, the correct pattern is: <code>NAME="VALUE"</code>`,
  'cc-env-var-editor-expert.errors.invalid-name': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`<code>${name}</code> is not a valid variable name`,
  'cc-env-var-editor-expert.errors.invalid-name-strict': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`<code>${name}</code> is not a valid variable name`,
  'cc-env-var-editor-expert.errors.invalid-value': () =>
    sanitize`the value is not valid, if you use quotes, you need to escape them like this: <code>\\"</code> or quote the whole value.`,
  'cc-env-var-editor-expert.errors.line': `line`,
  'cc-env-var-editor-expert.errors.unknown': `Unknown Error`,
  'cc-env-var-editor-expert.example': () =>
    sanitize`Format: <code>VARIABLE_NAME="variable value"</code> <br> Every variable must be separated by a line break, <cc-link href="${getDocUrl('/develop/env-variables/#format')}">learn more</cc-link>.`,
  'cc-env-var-editor-expert.info.java-prop': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Variable <code>${name}</code> will only be injected as a Java property and won't be part of the environment, <cc-link href="${getDocUrl('/develop/env-variables/#environment-variables-rules-and-formats')}">more details</cc-link>`,
  'cc-env-var-editor-expert.label': `Variable editing. Format: VARIABLE_NAME="variable value". Every variable must be separated by a line break.`,
  //#endregion
  //#region cc-env-var-editor-json
  'cc-env-var-editor-json.errors.duplicated-name': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`be careful, the name <code>${name}</code> is already defined`,
  'cc-env-var-editor-json.errors.invalid-json': `The JSON entered is invalid`,
  'cc-env-var-editor-json.errors.invalid-json-entry': () =>
    sanitize`The input was a valid JSON array of objects but all entries must have properties <code>name</code> and <code>value</code> of type string. Ex: <code>[{ "name": "THE_NAME", "value": "the value" }]</code>`,
  'cc-env-var-editor-json.errors.invalid-json-format': `The input was valid JSON but it does not follow the correct format. It must be an array of objects`,
  'cc-env-var-editor-json.errors.invalid-name': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`<code>${name}</code> is not a valid variable name`,
  'cc-env-var-editor-json.errors.invalid-name-strict': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`<code>${name}</code> is not a valid variable name in strict mode`,
  'cc-env-var-editor-json.errors.unknown': `Unknown Error`,
  'cc-env-var-editor-json.example': () =>
    sanitize`Format: <code>{ "name": "VARIABLE_NAME", "value": "variable value" }</code> <br> Array of objects following the above format, <cc-link href="${getDocUrl('/develop/env-variables/#format')}">learn more</cc-link>.`,
  'cc-env-var-editor-json.info.java-prop': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Variable <code>${name}</code> will only be injected as a Java property and won't be part of the environment, <cc-link href="${getDocUrl('/develop/env-variables/#environment-variables-rules-and-formats')}">more details</cc-link>`,
  'cc-env-var-editor-json.label': `Variable editing. Array of objects following the format: { "name": "VARIABLE_NAME", "value": "variable value" }.`,
  //#endregion
  //#region cc-env-var-editor-simple
  'cc-env-var-editor-simple.empty-data': `There are no variables.`,
  //#endregion
  //#region cc-env-var-form
  'cc-env-var-form.cli.content.add-var-command': `Add or update an environment variable:`,
  'cc-env-var-form.cli.content.get-file-var-command': `Get a sourceable env file:`,
  'cc-env-var-form.cli.content.instruction': getCliInstructions,
  'cc-env-var-form.cli.content.intro': `
      You can manage environment variables directly from your terminal using the commands below.
    `,
  'cc-env-var-form.cli.content.list-var-command': `List environment variables:`,
  'cc-env-var-form.description.config-provider': /** @param {{addonName: string}} _ */ ({ addonName }) =>
    sanitize`These values will be injected as environment variables in applications that have the add-on <strong>${addonName}</strong> in their service dependencies.<br>Every time you update your changes, all the dependent applications will be automatically restarted.`,
  'cc-env-var-form.description.env-var': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`These variables will be injected as environment variables in the application <strong>${appName}</strong>.`,
  'cc-env-var-form.description.exposed-config': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`Configuration exposed to dependent applications. <cc-link href="${getDocUrl('/administrate/service-dependencies/#exposed-configuration')}">Learn more</cc-link><br>These variables won't be injected in the application <strong>${appName}</strong>, they will be injected as environment variables in applications that have <strong>${appName}</strong> in their service dependencies.`,
  'cc-env-var-form.documentation.text': `Environment variables - Reference`,
  'cc-env-var-form.error.loading': `Something went wrong while loading variables.`,
  'cc-env-var-form.heading.config-provider': `Variables`,
  'cc-env-var-form.heading.env-var': `Environment variables`,
  'cc-env-var-form.heading.exposed-config': `Exposed configuration`,
  'cc-env-var-form.mode.expert': `Expert`,
  'cc-env-var-form.mode.simple': `Simple`,
  'cc-env-var-form.redeploy.error': `Something went wrong while trying to restart your application`,
  'cc-env-var-form.redeploy.error.app-stopped': `The app has never been deployed so it cannot be redeployed. Push a commit to start a deployment.`,
  'cc-env-var-form.redeploy.success.heading': `Restart in progress`,
  'cc-env-var-form.redeploy.success.text': /** @param {{ logsUrl: string }} _ */ ({ logsUrl }) =>
    sanitize`The process of restarting your application is in progress. See the <cc-link href="${logsUrl}">logs</cc-link>.`,
  'cc-env-var-form.reset': `Reset changes`,
  'cc-env-var-form.restart-app': `Restart the app to apply changes`,
  'cc-env-var-form.update': `Update changes`,
  'cc-env-var-form.update.error': `Something went wrong while updating variables.`,
  'cc-env-var-form.update.success': `Variables have been updated successfully.`,
  //#endregion
  //#region cc-env-var-input
  'cc-env-var-input.delete-button': `Remove`,
  'cc-env-var-input.keep-button': `Keep`,
  'cc-env-var-input.value-label': /** @param {{variableName: string}} _ */ ({ variableName }) =>
    `${variableName} value`,
  'cc-env-var-input.value-placeholder': `variable value`,
  //#endregion
  //#region cc-env-var-linked-services
  'cc-env-var-linked-services.description.addon': /** @param {{serviceName: string, appName: string}} _ */ ({
    serviceName,
    appName,
  }) => {
    return sanitize`List of variables exposed by the add-on <strong>${serviceName}</strong>.<br>Those variables will be injected as environment variables in the application <strong>${appName}</strong>.`;
  },
  'cc-env-var-linked-services.description.app': /** @param {{serviceName: string, appName: string}} _ */ ({
    serviceName,
    appName,
  }) => {
    return sanitize`Configuration exposed by the application <strong>${serviceName}</strong>.<br>Those variables will be injected as environement variables in the application <strong>${appName}</strong>.`;
  },
  'cc-env-var-linked-services.empty.addon': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`No add-ons linked to <strong>${appName}</strong>.`,
  'cc-env-var-linked-services.empty.app': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`No applications linked to <strong>${appName}</strong>.`,
  'cc-env-var-linked-services.error.addon': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`Something went wrong while loading add-ons linked to <strong>${appName}</strong>.`,
  'cc-env-var-linked-services.error.addon.without-app-name': `Something went wrong while loading add-ons linked to the application.`,
  'cc-env-var-linked-services.error.app': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`Something went wrong while loading applications linked to <strong>${appName}</strong>.`,
  'cc-env-var-linked-services.error.app.without-app-name': `Something went wrong while loading applications linked to the application.`,
  'cc-env-var-linked-services.heading.addon': /** @param {{name: string}} _ */ ({ name }) => `Add-on: ${name}`,
  'cc-env-var-linked-services.heading.app': /** @param {{name: string}} _ */ ({ name }) => `Application: ${name}`,
  'cc-env-var-linked-services.loading.addon': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`Loading variables exposed by add-ons linked to <strong>${appName}</strong>...`,
  'cc-env-var-linked-services.loading.app': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`Loading configuration exposed by applications linked to <strong>${appName}</strong>...`,
  //#endregion
  //#region cc-feature-list
  'cc-feature-list.documentation-link': `See documentation`,
  'cc-feature-list.error': `Something went wrong while loading available experimental features`,
  'cc-feature-list.feedback-link': `Share feedback`,
  'cc-feature-list.no-data': `No experimental features are available at this time`,
  'cc-feature-list.notice': `Changes to experimental features require a page reload to take effect`,
  'cc-feature-list.status.alpha': `Alpha`,
  'cc-feature-list.status.beta': `Beta`,
  'cc-feature-list.status.preview': `Preview`,
  'cc-feature-list.title': `Experimental Features`,
  //#endregion
  //#region cc-grafana-info
  'cc-grafana-info.disable-description': `Disabling Grafana will delete and end all accesses to this Grafana organisation. You will still be able to create another one.`,
  'cc-grafana-info.disable-title': `Disable Grafana`,
  'cc-grafana-info.disable.error': `Something went wrong while disabling Grafana dashboards.`,
  'cc-grafana-info.disable.success': `Grafana dashboards have been disabled successfully.`,
  'cc-grafana-info.documentation-description': `This service is used to visualize the our metrics data. You can find the documentation and details about those metrics here.`,
  'cc-grafana-info.documentation-title': `Documentation`,
  'cc-grafana-info.documentation.text': `Metrics with Grafana - Documentation`,
  'cc-grafana-info.enable-description': `Enabling Grafana will create and provide all accesses to a Grafana organisation.`,
  'cc-grafana-info.enable-title': `Enable Grafana`,
  'cc-grafana-info.enable.error': `Something went wrong while enabling Grafana dashboards.`,
  'cc-grafana-info.enable.success': `Grafana dashboards have been enabled successfully.`,
  'cc-grafana-info.error-link-grafana': `Something went wrong while loading Grafana link.`,
  'cc-grafana-info.error-loading': `Something went wrong while loading Grafana state.`,
  'cc-grafana-info.grafana-link-description': `Link to the Grafana which contains Clever Cloud metrics dashboards.`,
  'cc-grafana-info.grafana-link-title': `Grafana`,
  'cc-grafana-info.link.doc': `Read the documentation`,
  'cc-grafana-info.link.grafana': `Open Grafana`,
  'cc-grafana-info.loading-title': `Grafana`,
  'cc-grafana-info.main-title': `Metrics with Grafana`,
  'cc-grafana-info.reset-description': `Reset all Clever Cloud dashboards to their initial state.`,
  'cc-grafana-info.reset-title': `Reset all dashboards`,
  'cc-grafana-info.reset.error': `Something went wrong while resetting Grafana dashboards.`,
  'cc-grafana-info.reset.success': `Grafana dashboards have been reset successfully.`,
  'cc-grafana-info.screenshot.addon.alt': `Screenshot of an add-on dashboard in Grafana`,
  'cc-grafana-info.screenshot.addon.description': () =>
    sanitize`This dashboard includes several graphs about an add-on. <br> First, it displays a panel containing system metrics like <strong>CPU, memory, disks and network</strong>. <br> For <strong>MySQL, PostgreSQL, MongoDB and Redis</strong> add-ons, a second panel will contain databases data and provides information like <strong>connections count, queries or transactions, errors or deadlocks and tuples operations<strong>.`,
  'cc-grafana-info.screenshot.addon.title': `Add-on dashboard preview`,
  'cc-grafana-info.screenshot.organisation.alt': `Screenshot of an organisation dashboard in Grafana`,
  'cc-grafana-info.screenshot.organisation.description': () =>
    sanitize`This dashboard includes several graphs about a Clever Cloud organisation. <br> It provides a graph summarizing the amount of <strong>deployed application runtimes and add-ons</strong>. It also provides the number of services <strong>by type</strong> or <strong>by plan (flavor)</strong>. <br> The <strong>state graph</strong> indicates a state for all deployments performed during the elapsed Grafana time interval. <br> And finally on this dashboard you will retrieve <strong>global and specific links</strong> (sorted by requests count) to access an application runtime or add-on dashboard.`,
  'cc-grafana-info.screenshot.organisation.title': `Organisation dashboard preview`,
  'cc-grafana-info.screenshot.runtime.alt': `Screenshot of an application runtime dashboard in Grafana`,
  'cc-grafana-info.screenshot.runtime.description': () =>
    sanitize`This dashboard features an <strong>overview panel</strong> to get quick information about an application, as well as several panels about its system metrics. <br> It provides several graphs summarizing the state of <strong>CPU, memory, disks and network</strong>. <br> For each of these metric types, you will retrieve usage graphs, gauges, as well as the result of a simple linear prediction done on the elapsed Grafana time interval. This linear prediction yields the expected duration for the metric to reach 90%.`,
  'cc-grafana-info.screenshot.runtime.title': `Application runtime dashboard preview`,
  //#endregion
  //#region cc-header-app
  'cc-header-app.action.cancel-deployment': `Cancel deployment`,
  'cc-header-app.action.restart': `Restart`,
  'cc-header-app.action.restart-last-commit': `Restart last pushed commit`,
  'cc-header-app.action.restart-rebuild': `Re-build and restart`,
  'cc-header-app.action.start': `Start`,
  'cc-header-app.action.start-last-commit': `Start last pushed commit`,
  'cc-header-app.action.start-rebuild': `Re-build and start`,
  'cc-header-app.action.stop': `Stop app`,
  'cc-header-app.commits.git': /** @param {{commit: string}} _ */ ({ commit }) => `git repo version (HEAD): ${commit}`,
  'cc-header-app.commits.no-commits': `no commits yet`,
  'cc-header-app.commits.running': /** @param {{commit: string}} _ */ ({ commit }) => `running version: ${commit}`,
  'cc-header-app.commits.starting': /** @param {{commit: string}} _ */ ({ commit }) => `deploying version: ${commit}`,
  'cc-header-app.disable-buttons': `You are not authorized to perform those actions`,
  'cc-header-app.error': `Something went wrong while loading app info.`,
  'cc-header-app.read-logs': `read logs here`,
  'cc-header-app.state-msg.app-is-restarting': `Your application is restarting...`,
  'cc-header-app.state-msg.app-is-running': `Your application is running!`,
  'cc-header-app.state-msg.app-is-starting': `Your application is starting...`,
  'cc-header-app.state-msg.app-is-stopped': `Your application is currently stopped.`,
  'cc-header-app.state-msg.last-deploy-failed': `The last deployment failed,`,
  'cc-header-app.state-msg.unknown-state': `Unknown state, try to restart the application or contact our support if you have additional questions.`,
  'cc-header-app.user-action-msg.app-will-start': `Your application is about to start...`,
  'cc-header-app.user-action-msg.app-will-stop': `Your application is about to stop...`,
  'cc-header-app.user-action-msg.deploy-cancelled': `This deployment has been cancelled.`,
  'cc-header-app.user-action-msg.deploy-will-begin': `A new deployment is about to begin...`,
  //#endregion
  //#region cc-header-orga
  'cc-header-orga.error': `Something went wrong while loading organisation info.`,
  'cc-header-orga.hotline': `Hotline:`,
  //#endregion
  //#region cc-heptapod-info
  'cc-heptapod-info.description': () =>
    sanitize`This Heptapod instance hosts mercurial projects. Learn more on <cc-link href="https://about.heptapod.host">https://about.heptapod.host</cc-link>.`,
  'cc-heptapod-info.documentation.text': `Heptapod - Documentation`,
  'cc-heptapod-info.error-loading': `Something went wrong while loading usage info.`,
  'cc-heptapod-info.not-in-use': `You are not using this Heptapod service.`,
  'cc-heptapod-info.price-description': `Estimated price`,
  'cc-heptapod-info.price-value': /** @param {{price: number}} _ */ ({ price }) =>
    `${formatCurrency(lang, price)} / month`,
  'cc-heptapod-info.private-active-users-description': `Private users`,
  'cc-heptapod-info.public-active-users-description': `Public users`,
  'cc-heptapod-info.storage-bytes': /** @param {{storage: number}} _ */ ({ storage }) => formatBytes(storage, 1),
  'cc-heptapod-info.storage-description': `Storage size`,
  //#endregion
  //#region cc-homepage-onboarding
  'cc-homepage-onboarding.card.cli.button': `Install CLI`,
  'cc-homepage-onboarding.card.cli.description': `Manage apps, services, scale resources, and streamline workflows directly from your terminal.`,
  'cc-homepage-onboarding.card.cli.title': `Use Clever Tools CLI`,
  'cc-homepage-onboarding.card.config-payment.button': `Add payment method`,
  'cc-homepage-onboarding.card.config-payment.description': `To avoid any suspension of your services and deletion of your data, add a valid payment method.`,
  'cc-homepage-onboarding.card.config-payment.title': `Configure payment`,
  'cc-homepage-onboarding.card.first-resource.button': `Create new resource`,
  'cc-homepage-onboarding.card.first-resource.description': `Add applications, add-ons, databases, storage...`,
  'cc-homepage-onboarding.card.first-resource.title': `Deploy your first resource`,
  'cc-homepage-onboarding.card.new-organisation.button': `Create new organisation`,
  'cc-homepage-onboarding.card.new-organisation.description': `Create a new organisation to group and launch applications, add-ons, databases, storage...`,
  'cc-homepage-onboarding.card.new-organisation.title': `Create new organisation`,
  'cc-homepage-onboarding.card.new-resource.button': `Add new resource`,
  'cc-homepage-onboarding.card.new-resource.description': `Deploy applications, add-ons, databases, storage...`,
  'cc-homepage-onboarding.card.new-resource.select.placeholder': `-- Select --`,
  'cc-homepage-onboarding.card.new-resource.select.title': `Organisation`,
  'cc-homepage-onboarding.card.new-resource.title': `Launch new resource`,
  'cc-homepage-onboarding.card.secure.button': `Activate 2FA`,
  'cc-homepage-onboarding.card.secure.description': `Enhance your account security with Two-factor authentication (2FA), highly recommended to protect your access.`,
  'cc-homepage-onboarding.card.secure.title': `Secure your account`,
  'cc-homepage-onboarding.card.ssh-keys.button': `Add SSH key`,
  'cc-homepage-onboarding.card.ssh-keys.description': `Secure your connections and simplify server access with SSH key authentication.`,
  'cc-homepage-onboarding.card.ssh-keys.title': `Configure SSH keys`,
  'cc-homepage-onboarding.card.support.button': `Contact support`,
  'cc-homepage-onboarding.card.support.description': `Get personalized assistance from our dedicated support team whenever you need it.`,
  'cc-homepage-onboarding.card.support.title': `Contact expert`,
  'cc-homepage-onboarding.close': `Close this onboarding block`,
  'cc-homepage-onboarding.description.already-user': `Choose your next action and keep building amazing things`,
  'cc-homepage-onboarding.description.new-user': `Get started by creating your first project. Join Clever Cloud's ecosystem and experience seamless deployment. New to the platform? Try our ready-to-deploy projects or reach out for personalized assistance.`,
  'cc-homepage-onboarding.error': `Something went wrong while loading the onboarding data.`,
  'cc-homepage-onboarding.title.already-user': `Continue to create`,
  'cc-homepage-onboarding.title.new-user': `Ready to start and deploy?`,
  //#endregion
  //#region cc-homepage-template-project
  'cc-homepage-template-project.error': `An error occurred while loading template projects`,
  'cc-homepage-template-project.title': `Ready-to-deploy projects`,
  //#endregion
  //#region cc-homepage-video
  'cc-homepage-video.iframe-title': `YouTube video`,
  'cc-homepage-video.link': `Access our YouTube channel`,
  'cc-homepage-video.play': `YouTube video thumbnail Play video`,
  'cc-homepage-video.title': `Watch our videos`,
  //#endregion
  //#region cc-input-date
  'cc-input-date.error.bad-input': /** @param {{ date: string }} _ */ ({ date }) =>
    sanitize`You must enter a date. <br> For instance: ${date}.`,
  'cc-input-date.error.empty': `You must enter a value`,
  'cc-input-date.error.range-overflow': /** @param {{max: string}} _ */ ({ max }) =>
    `You must enter a date lower than ${max}.`,
  'cc-input-date.error.range-underflow': /** @param {{min: string}} _ */ ({ min }) =>
    `You must enter a date higher than ${min}.`,
  'cc-input-date.help': `Format: YYYY-MM-DD HH:MM:SS`,
  'cc-input-date.keyboard-hint': `You can use up or down arrow keys to modify parts of the date.`,
  'cc-input-date.required': `required`,
  //#endregion
  //#region cc-input-number
  'cc-input-number.decrease': `decrease`,
  'cc-input-number.error.bad-type': `You must enter a number`,
  'cc-input-number.error.empty': `You must enter a value`,
  'cc-input-number.error.range-overflow': /** @param {{max: number}} _ */ ({ max }) =>
    `You must enter a number lower that ${max}.`,
  'cc-input-number.error.range-underflow': /** @param {{min: number}} _ */ ({ min }) =>
    `You must enter a number higher that ${min}.`,
  'cc-input-number.increase': `increase`,
  'cc-input-number.required': `required`,
  //#endregion
  //#region cc-input-text
  'cc-input-text.error.bad-email': () => sanitize`Invalid email address format.<br>Example: john.doe@example.com.`,
  'cc-input-text.error.empty': `You must enter a value`,
  'cc-input-text.error.empty.email': `Enter an email address`,
  'cc-input-text.required': `required`,
  'cc-input-text.secret.hide': `Hide secret`,
  'cc-input-text.secret.show': `Show secret`,
  //#endregion
  //#region cc-invoice
  'cc-invoice.download-pdf': `Download PDF`,
  'cc-invoice.error': `Something went wrong while loading the invoice.`,
  'cc-invoice.info': /** @param {{date: string, amount: number, currency: string}} _ */ ({
    date,
    amount,
    currency,
  }) => {
    return sanitize`This invoice was issued on <strong>${formatDateOnly(date)}</strong> for a total amount of <strong>${formatCurrency(lang, amount, { currency })}</strong>.`;
  },
  'cc-invoice.title': `Invoice`,
  //#endregion
  //#region cc-invoice-list
  'cc-invoice-list.documentation.text': `Clever Cloud billing - Documentation`,
  'cc-invoice-list.error': `An error occurred while loading invoices.`,
  'cc-invoice-list.pending': `Pending invoices`,
  'cc-invoice-list.pending.no-invoices': `No pending invoices at the moment.`,
  'cc-invoice-list.processed': `Paid invoices`,
  'cc-invoice-list.processed.no-invoices': `No paid invoices at the moment.`,
  'cc-invoice-list.processing': `Invoices awaiting payment validation`,
  'cc-invoice-list.title': `Invoices`,
  'cc-invoice-list.year': `Year:`,
  //#endregion
  //#region cc-invoice-table
  'cc-invoice-table.date.emission': `Emission date`,
  'cc-invoice-table.date.value': /** @param {{date: string}} _ */ ({ date }) => `${formatDateOnly(date)}`,
  'cc-invoice-table.number': `Number`,
  'cc-invoice-table.open-pdf': `Download PDF`,
  'cc-invoice-table.pay': `Pay`,
  'cc-invoice-table.text': /** @param {{number: string, date: string|number, amount: number, currency: string}} _ */ ({
    number,
    date,
    amount,
    currency,
  }) => {
    return sanitize`Invoice <strong>${number}</strong> issued on <strong>${formatDateOnly(date)}</strong> for a total amount of <code>${formatCurrency(lang, amount, { currency })}</code>`;
  },
  'cc-invoice-table.total.label': `Total`,
  'cc-invoice-table.total.value': /** @param {{amount: number, currency: string}} _ */ ({ amount, currency }) =>
    `${formatCurrency(lang, amount, { currency })}`,
  //#endregion
  //#region cc-kv-explorer
  'cc-kv-explorer.details.empty': `Select a key in the list to get its content`,
  'cc-kv-explorer.details.unsupported': `The selected key type is not supported`,
  'cc-kv-explorer.error.add-key': `An error occurred while adding the key`,
  'cc-kv-explorer.error.delete-key': `An error occurred while deleting the key`,
  'cc-kv-explorer.error.fetch-keys.filtering': `An error occurred while filtering keys.`,
  'cc-kv-explorer.error.fetch-keys.loading': `An error occurred while loading keys.`,
  'cc-kv-explorer.error.fetch-keys.loading-more': `An error occurred while loading more keys.`,
  'cc-kv-explorer.error.fetch-keys.refreshing': `An error occurred while refreshing keys.`,
  'cc-kv-explorer.error.fetch-keys.retry': `Retry`,
  'cc-kv-explorer.error.get-key': `An error occurred while fetching the key`,
  'cc-kv-explorer.error.key-doesnt-exist': `The key doesn't exist anymore`,
  'cc-kv-explorer.filter.all': `All`,
  'cc-kv-explorer.filter.apply': `Apply the filter`,
  'cc-kv-explorer.filter.by-pattern': `Filter keys by pattern`,
  'cc-kv-explorer.filter.by-type': `Filter by type`,
  'cc-kv-explorer.form.add': `Add`,
  'cc-kv-explorer.form.error.already-used': `Key name already used`,
  'cc-kv-explorer.form.key': `Key name`,
  'cc-kv-explorer.form.reset': `Cancel`,
  'cc-kv-explorer.form.string.value': `Value`,
  'cc-kv-explorer.form.type': `Key type`,
  'cc-kv-explorer.key.delete': /** @param {{index: number}} _ */ ({ index }) => `Delete the key at index ${index}`,
  'cc-kv-explorer.key.header.copy': `Copy the key name to clipboard`,
  'cc-kv-explorer.key.header.delete': `Delete key`,
  'cc-kv-explorer.key.type.hash': `Hash`,
  'cc-kv-explorer.key.type.list': `List`,
  'cc-kv-explorer.key.type.set': `Set`,
  'cc-kv-explorer.key.type.string': `String`,
  'cc-kv-explorer.keys.empty': `Database is empty!`,
  'cc-kv-explorer.keys.empty.create-key': `Create a new key`,
  'cc-kv-explorer.keys.header.add-key': `key`,
  'cc-kv-explorer.keys.header.add-key.a11y': `Add new key`,
  'cc-kv-explorer.keys.header.refresh': `Refresh`,
  'cc-kv-explorer.keys.header.total': /** @param {{total: number}} _ */ ({ total }) =>
    `${formatNumber(lang, total)} ${plural(total, 'key')}`,
  'cc-kv-explorer.keys.no-results': `No results matching the filter`,
  'cc-kv-explorer.loading.error': `An error occurred while connecting to the database`,
  'cc-kv-explorer.success.add-key': `The key has been added`,
  'cc-kv-explorer.success.delete-key': `The key has been deleted`,
  //#endregion
  //#region cc-kv-hash-explorer
  'cc-kv-hash-explorer.add-form.element-field': `Field`,
  'cc-kv-hash-explorer.add-form.element-value': `Value`,
  'cc-kv-hash-explorer.add-form.submit': `Add`,
  'cc-kv-hash-explorer.add-form.submit.a11y': `Add the field`,
  'cc-kv-hash-explorer.element.copy': /** @param {{index: number}} _ */ ({ index }) =>
    `Copy value at index ${index} to clipboard`,
  'cc-kv-hash-explorer.element.delete': /** @param {{index: number}} _ */ ({ index }) =>
    `Delete the field at index ${index}`,
  'cc-kv-hash-explorer.element.edit.cancel': `Cancel value edition`,
  'cc-kv-hash-explorer.element.edit.save': `Update the value`,
  'cc-kv-hash-explorer.element.edit.start': /** @param {{index: number}} _ */ ({ index }) =>
    `Edit the value at index ${index}`,
  'cc-kv-hash-explorer.element.edit.value-input': `Value`,
  'cc-kv-hash-explorer.error.apply-filter': `An error occurred while applying filter`,
  'cc-kv-hash-explorer.error.element-add': `An error occurred while adding the field`,
  'cc-kv-hash-explorer.error.element-delete': `An error occurred while deleting the field`,
  'cc-kv-hash-explorer.error.element-update': `An error occurred while updating the value`,
  'cc-kv-hash-explorer.error.fetch-elements': `An error occurred while fetching fields`,
  'cc-kv-hash-explorer.filter': `Filter by field`,
  'cc-kv-hash-explorer.filter.apply': `Apply the filter`,
  'cc-kv-hash-explorer.header.field': `Field`,
  'cc-kv-hash-explorer.header.value': `Value`,
  'cc-kv-hash-explorer.no-results': `No results matching the filter`,
  'cc-kv-hash-explorer.success.element-add': `The field has been added`,
  'cc-kv-hash-explorer.success.element-delete': `The field has been deleted`,
  'cc-kv-hash-explorer.success.element-update': `The value has been updated`,
  //#endregion
  //#region cc-kv-hash-input
  'cc-kv-hash-input.element.add': `Add a new field`,
  'cc-kv-hash-input.element.delete': /** @param {{index: number}} _ */ ({ index }) =>
    `Remove the field at index ${index}`,
  'cc-kv-hash-input.element.field-input': `Field`,
  'cc-kv-hash-input.element.value-input': `Value`,
  'cc-kv-hash-input.header.field': `Field`,
  'cc-kv-hash-input.header.value': `Value`,
  //#endregion
  //#region cc-kv-list-explorer
  'cc-kv-list-explorer.add-form.element-position': `Push to`,
  'cc-kv-list-explorer.add-form.element-position.head': `Head`,
  'cc-kv-list-explorer.add-form.element-position.tail': `Tail`,
  'cc-kv-list-explorer.add-form.element-value': `Element`,
  'cc-kv-list-explorer.add-form.submit': `Push`,
  'cc-kv-list-explorer.add-form.submit.a11y': `Push the element`,
  'cc-kv-list-explorer.element.copy': /** @param {{index: number}} _ */ ({ index }) =>
    `Copy value at index ${index} to clipboard`,
  'cc-kv-list-explorer.element.edit.cancel': `Cancel element edition`,
  'cc-kv-list-explorer.element.edit.save': `Update the element`,
  'cc-kv-list-explorer.element.edit.start': /** @param {{index: number}} _ */ ({ index }) =>
    `Edit the element at index ${index}`,
  'cc-kv-list-explorer.element.edit.value-input': `Element value`,
  'cc-kv-list-explorer.error.apply-filter': `An error occurred while applying filter`,
  'cc-kv-list-explorer.error.element-add': `An error occurred while adding the element`,
  'cc-kv-list-explorer.error.element-update': `An error occurred while updating the element`,
  'cc-kv-list-explorer.error.fetch-elements': `An error occurred while fetching elements`,
  'cc-kv-list-explorer.filter': `Filter by index`,
  'cc-kv-list-explorer.filter.apply': `Apply the filter`,
  'cc-kv-list-explorer.header.index': `Index`,
  'cc-kv-list-explorer.header.value': `Element`,
  'cc-kv-list-explorer.no-results': `No results matching the filter`,
  'cc-kv-list-explorer.success.element-add': /** @param {{index: number}} _ */ ({ index }) =>
    `The element has been added at index ${index}`,
  'cc-kv-list-explorer.success.element-update': `The element has been updated`,
  //#endregion
  //#region cc-kv-list-input
  'cc-kv-list-input.element.add': `Add a new element`,
  'cc-kv-list-input.element.delete': /** @param {{index: number}} _ */ ({ index }) =>
    `Remove the element at index ${index}`,
  'cc-kv-list-input.element.value-input': `Element`,
  'cc-kv-list-input.header.elements': `Elements`,
  //#endregion
  //#region cc-kv-set-explorer
  'cc-kv-set-explorer.add-form.element-value': `Member`,
  'cc-kv-set-explorer.add-form.submit': `Add`,
  'cc-kv-set-explorer.add-form.submit.a11y': `Add the member`,
  'cc-kv-set-explorer.element.copy': /** @param {{index: number}} _ */ ({ index }) =>
    `Copy value at index ${index} to clipboard`,
  'cc-kv-set-explorer.element.delete': /** @param {{index: number}} _ */ ({ index }) =>
    `Delete the member at index ${index}`,
  'cc-kv-set-explorer.error.apply-filter': `An error occurred while applying filter`,
  'cc-kv-set-explorer.error.element-add': `An error occurred while adding the member`,
  'cc-kv-set-explorer.error.element-delete': `An error occurred while deleting the member`,
  'cc-kv-set-explorer.error.fetch-elements': `An error occurred while fetching members`,
  'cc-kv-set-explorer.filter': `Filter`,
  'cc-kv-set-explorer.filter.apply': `Apply the filter`,
  'cc-kv-set-explorer.no-results': `No results matching the filter`,
  'cc-kv-set-explorer.success.element-add': `The member has been added`,
  'cc-kv-set-explorer.success.element-already-exist': `The member already existed`,
  'cc-kv-set-explorer.success.element-delete': `The member has been deleted`,
  //#endregion
  //#region cc-kv-string-editor
  'cc-kv-string-editor.error.update-value': `An error occurred while updating the value`,
  'cc-kv-string-editor.form.reset': `Cancel`,
  'cc-kv-string-editor.form.save': `Save`,
  'cc-kv-string-editor.form.value': `Value`,
  'cc-kv-string-editor.success.update-value': `The value has been updated`,
  //#endregion
  //#region cc-kv-terminal
  'cc-kv-terminal.header': `KV Explorer Terminal`,
  'cc-kv-terminal.shell.prompt': `Command line`,
  'cc-kv-terminal.warning': `All commands executed in this terminal are immediately sent to the database`,
  //#endregion
  //#region cc-link
  'cc-link.download.icon-a11y-name': `Download`,
  'cc-link.new-window.name': `new window`,
  'cc-link.new-window.title': /** @param {{linkText: string}} _ */ ({ linkText }) => `${linkText} - new window`,
  //#endregion
  //#region cc-loader
  'cc-loader.a11y-name': `Loading`,
  //#endregion
  //#region cc-logs
  'cc-logs.copied': /** @param {{count: number}} _ */ ({ count }) => `Copied (${count} ${plural(count, 'line')})`,
  'cc-logs.copy': `Copy`,
  'cc-logs.inspect': `Inspect`,
  'cc-logs.select-button.label': /** @param {{index: number}} _ */ ({ index }) => `Select the line ${index}`,
  'cc-logs.unselect-button.label': /** @param {{index: number}} _ */ ({ index }) => `Unselect the line ${index}`,
  //#endregion
  //#region cc-logs-addon-runtime
  'cc-logs-addon-runtime.fullscreen': `Fullscreen`,
  'cc-logs-addon-runtime.fullscreen.exit': `Exit fullscreen`,
  'cc-logs-addon-runtime.logs.error': `Something went wrong while loading logs`,
  'cc-logs-addon-runtime.logs.loading': `Looking for log entries...`,
  'cc-logs-addon-runtime.logs.warning.no-logs.message': `There are no logs matching the selected criteria`,
  'cc-logs-addon-runtime.logs.warning.no-logs.title': `No logs`,
  'cc-logs-addon-runtime.logs.warning.waiting.message': `New log entries emitted by the add-on will appear here`,
  'cc-logs-addon-runtime.logs.warning.waiting.title': `No logs to show right now`,
  //#endregion
  //#region cc-logs-app-access
  'cc-logs-app-access.error': `Something went wrong while loading logs`,
  'cc-logs-app-access.fullscreen': `Fullscreen`,
  'cc-logs-app-access.fullscreen.exit': `Exit fullscreen`,
  'cc-logs-app-access.loading': `Looking for log entries...`,
  'cc-logs-app-access.no-logs.message': `There are no logs matching the selected criteria`,
  'cc-logs-app-access.no-logs.title': `No logs`,
  'cc-logs-app-access.options.city': `Display city`,
  'cc-logs-app-access.options.country': `Display country`,
  'cc-logs-app-access.options.ip': `Display IP address`,
  'cc-logs-app-access.waiting.message': `New log entries emitted by the application will appear here`,
  'cc-logs-app-access.waiting.title': `No logs to show right now`,
  //#endregion
  //#region cc-logs-app-runtime
  'cc-logs-app-runtime.fullscreen': `Fullscreen`,
  'cc-logs-app-runtime.fullscreen.exit': `Exit fullscreen`,
  'cc-logs-app-runtime.logs.error': `Something went wrong while loading logs`,
  'cc-logs-app-runtime.logs.loading': `Looking for log entries...`,
  'cc-logs-app-runtime.logs.warning.no-logs.message': `There are no logs matching the selected criteria`,
  'cc-logs-app-runtime.logs.warning.no-logs.title': `No logs`,
  'cc-logs-app-runtime.logs.warning.waiting.message': `New log entries emitted by the application will appear here`,
  'cc-logs-app-runtime.logs.warning.waiting.title': `No logs to show right now`,
  'cc-logs-app-runtime.options.display-instance': `Display instance name`,
  //#endregion
  //#region cc-logs-control
  'cc-logs-control.date-display': `Date format`,
  'cc-logs-control.date-display.datetime-iso': `Date and hour ISO`,
  'cc-logs-control.date-display.datetime-short': `Date and hour`,
  'cc-logs-control.date-display.none': `Do not display`,
  'cc-logs-control.date-display.time-iso': `Hour ISO`,
  'cc-logs-control.date-display.time-short': `Hour`,
  'cc-logs-control.option-header.date': `Date`,
  'cc-logs-control.option-header.display': `Display`,
  'cc-logs-control.option-header.metadata': `Metadata`,
  'cc-logs-control.palette': `Color scheme`,
  'cc-logs-control.palette.default': `Default scheme`,
  'cc-logs-control.scroll-to-bottom': `Scroll to bottom`,
  'cc-logs-control.show-logs-options': `Options`,
  'cc-logs-control.strip-ansi': `Strip ANSI escape code`,
  'cc-logs-control.timezone': `Time zone`,
  'cc-logs-control.timezone.local': `Local`,
  'cc-logs-control.timezone.utc': `UTC`,
  'cc-logs-control.wrap-lines': `Force lines wrap`,
  //#endregion
  //#region cc-logs-date-range-selector
  'cc-logs-date-range-selector.custom-date-range.apply': `Apply`,
  'cc-logs-date-range-selector.custom-date-range.next': `Shift to next range`,
  'cc-logs-date-range-selector.custom-date-range.previous': `Shift to previous range`,
  'cc-logs-date-range-selector.custom-date-range.since.local': `Since (local time)`,
  'cc-logs-date-range-selector.custom-date-range.since.utc': `Since (UTC)`,
  'cc-logs-date-range-selector.custom-date-range.until.local': `Until (local time)`,
  'cc-logs-date-range-selector.custom-date-range.until.utc': `Until (UTC)`,
  'cc-logs-date-range-selector.option.custom': `Custom`,
  'cc-logs-date-range-selector.option.last-4-hours': `Last 4 hours`,
  'cc-logs-date-range-selector.option.last-7-days': `Last 7 days`,
  'cc-logs-date-range-selector.option.last-hour': `Last hour`,
  'cc-logs-date-range-selector.option.live': `Live view`,
  'cc-logs-date-range-selector.option.today': `Today`,
  'cc-logs-date-range-selector.option.yesterday': `Yesterday`,
  //#endregion
  //#region cc-logs-instances
  'cc-logs-instances.cold.empty': `No instance`,
  'cc-logs-instances.cold.header': `Instances`,
  'cc-logs-instances.commit.title': /** @param {{commit: string}} _ */ ({ commit }) => `Deployed commit: ${commit}`,
  'cc-logs-instances.deleted.header': `Deleted instances`,
  'cc-logs-instances.deploying.header': `Deployment in progress`,
  'cc-logs-instances.deployment.deployed': `Deployed`,
  'cc-logs-instances.deployment.state.cancelled': `Deployment cancelled`,
  'cc-logs-instances.deployment.state.failed': `Deployment failed`,
  'cc-logs-instances.deployment.state.succeeded': `Deployment succeeded`,
  'cc-logs-instances.deployment.state.wip': `Deployment in progress`,
  'cc-logs-instances.ghost.header': `Ghost instances`,
  'cc-logs-instances.ghost.notice': `Some unwanted instances are still running, but will soon be destroyed by our scheduler. You can contact support to get more information.`,
  'cc-logs-instances.instance.build': `Build instance`,
  'cc-logs-instances.instance.index': /** @param {{index: number}} _ */ ({ index }) => `Instance #${index}`,
  'cc-logs-instances.instance.state.deleted': `Deleted instance`,
  'cc-logs-instances.instance.state.deploying': `Deploying instance`,
  'cc-logs-instances.instance.state.running': `Running instance`,
  'cc-logs-instances.instance.state.stopping': `Stopping instance`,
  'cc-logs-instances.loading.error': `Something went wrong while loading instances.`,
  'cc-logs-instances.loading.loader': `Instances are loading`,
  'cc-logs-instances.running.empty': `No instance running`,
  'cc-logs-instances.running.header': `Running instances`,
  'cc-logs-instances.stopping.header': `Stopping instances`,
  //#endregion
  //#region cc-logs-loading-progress
  'cc-logs-loading-progress.control.pause': `Pause`,
  'cc-logs-loading-progress.control.resume': `Resume`,
  'cc-logs-loading-progress.overflow.accept': `Continue`,
  'cc-logs-loading-progress.overflow.discard': `Stop`,
  'cc-logs-loading-progress.overflow.info': /** @param {{limit: number}} _ */ ({ limit }) =>
    `Only the last ${formatNumber(lang, limit)} logs are displayed`,
  'cc-logs-loading-progress.overflow.warning': /** @param {{limit: number}} _ */ ({ limit }) =>
    `You'll soon reach ${formatNumber(lang, limit)} logs loaded. What do you want to do?`,
  'cc-logs-loading-progress.progress.indeterminate': /** @param {{count: number}} _ */ ({ count }) =>
    `${formatNumber(lang, count)} logs loaded`,
  'cc-logs-loading-progress.progress.none': `No logs loaded`,
  'cc-logs-loading-progress.progress.percentage': /** @param {{count: number, percent: number}} _ */ ({
    count,
    percent,
  }) => `${formatNumber(lang, count)} logs loaded (${formatPercent(lang, percent)})`,
  //#endregion
  //#region cc-logs-message-filter
  'cc-logs-message-filter.bad-format': `Invalid regex`,
  'cc-logs-message-filter.label': `Filter`,
  'cc-logs-message-filter.mode.regex': `Filter with a regular expression`,
  'cc-logs-message-filter.mode.strict': `Filter with an exact string`,
  //#endregion
  //#region cc-logsmap
  'cc-logsmap.legend.heatmap': /** @param {{orgaName: string}} _ */ ({ orgaName }) =>
    sanitize`Heatmap of HTTP requests received by all apps from <strong>${orgaName}</strong> during the last 24 hours.`,
  'cc-logsmap.legend.heatmap.app': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`Heatmap of HTTP requests received by <strong>${appName}</strong> during the last 24 hours.`,
  'cc-logsmap.legend.points': /** @param {{orgaName: string}} _ */ ({ orgaName }) =>
    sanitize`Realtime map of HTTP requests received by all apps from <strong>${orgaName}</strong>.`,
  'cc-logsmap.legend.points.app': /** @param {{appName: string}} _ */ ({ appName }) =>
    sanitize`Realtime map of HTTP requests received by <strong>${appName}</strong>.`,
  'cc-logsmap.mode.heatmap': `Last 24h`,
  'cc-logsmap.mode.points': `Realtime`,
  //#endregion
  //#region cc-map
  'cc-map.error': `Something went wrong while trying to fetch data for the map.`,
  'cc-map.error.icon-a11y-name': `Warning`,
  'cc-map.no-points': `No data to display on the map right now.`,
  //#endregion
  //#region cc-network-group-dashboard
  'cc-network-group-dashboard.danger-zone.btn': `Delete Network Group`,
  'cc-network-group-dashboard.danger-zone.desc': () =>
    sanitize`Deleting this Network Group is a permanent action.<br>Resources that were members will be unlinked and unchanged.`,
  'cc-network-group-dashboard.danger-zone.dialog.confirm-input-label': `Enter the Network Group name`,
  'cc-network-group-dashboard.danger-zone.dialog.desc': `Deleting this Network Group is permanent. All its members will be unlinked. Before proceeding, make sure resources that depend on it (apps calling an add-on through its NG domain name, external peers configured on the tunnel) have an alternative path.`,
  'cc-network-group-dashboard.danger-zone.dialog.heading': `Confirm deletion`,
  'cc-network-group-dashboard.danger-zone.error': `Something went wrong while loading information about the Network Group`,
  'cc-network-group-dashboard.danger-zone.heading': `Danger Zone`,
  'cc-network-group-dashboard.delete.error': `Something went wrong while deleting the Network Group`,
  'cc-network-group-dashboard.delete.success': `The Network Group has been deleted successfully`,
  'cc-network-group-dashboard.doc-link.text': `Network Groups - Documentation`,
  //#endregion
  //#region cc-network-group-list
  'cc-network-group-list.cli.content.create-command': `Create a Network Group:`,
  'cc-network-group-list.cli.content.delete-command': `Delete a Network Group:`,
  'cc-network-group-list.cli.content.instruction': getCliInstructions,
  'cc-network-group-list.cli.content.intro': `You can manage Network Groups directly from your terminal using the commands below.`,
  'cc-network-group-list.cli.content.link-command': `Link a Network Group to your resource:`,
  'cc-network-group-list.cli.content.list-command': `List Network Groups:`,
  'cc-network-group-list.cli.content.unlink-command': `Unlink a Network Group from your resource:`,
  'cc-network-group-list.create': `Create a Network Group`,
  'cc-network-group-list.form.button': `Link to the Network Group`,
  'cc-network-group-list.form.description': `This screen lists the Network Groups this resource is a member of. Within a Network Group, the resource (an application or an add-on) is reachable by a domain name, and each of its instances becomes a peer reachable by an IP address. You can also link this resource to another Network Group.`,
  'cc-network-group-list.form.documentation': `Network Groups - Documentation`,
  'cc-network-group-list.form.error': `Something went wrong while loading the form`,
  'cc-network-group-list.form.heading': `Link the resource to a Network Group`,
  'cc-network-group-list.form.select-label': `Choose a Network Group to link`,
  'cc-network-group-list.link.error': `Something went wrong while linking the Network Group`,
  'cc-network-group-list.link.success': `The Network Group has been linked successfully`,
  'cc-network-group-list.list.dashboard-link': `Go to the Network Group`,
  'cc-network-group-list.list.empty': `No Network Group is linked to this resource`,
  'cc-network-group-list.list.error': `Something went wrong while loading the list of linked Network Groups`,
  'cc-network-group-list.list.heading': `Linked Network Groups`,
  'cc-network-group-list.list.unlink': `Unlink Network Group`,
  'cc-network-group-list.list.unlink.a11y-name': /** @param {{ name: string }} _ */ ({ name }) =>
    `Unlink Network Group ${name}`,
  'cc-network-group-list.refresh.error': `The resource has been linked to the Network Group, but the list could not be refreshed. Refresh the page.`,
  'cc-network-group-list.unlink.dialog.desc': `This resource and all its instances will be disconnected from the Network Group's private network. Other resources in the group will no longer be able to reach it. Continue?`,
  'cc-network-group-list.unlink.dialog.heading': `Confirm unlink`,
  'cc-network-group-list.unlink.dialog.unlink-btn': `Confirm and unlink`,
  'cc-network-group-list.unlink.error': `Something went wrong while unlinking the Network Group`,
  'cc-network-group-list.unlink.success': `The Network Group has been unlinked successfully`,
  'cc-network-group-list.unsupported-notice.heading': `Migrate to paid plan`,
  'cc-network-group-list.unsupported-notice.message': /** @param {{addonMigrationScreenUrl: string}} _ */ ({
    addonMigrationScreenUrl,
  }) =>
    sanitize`Add-ons on a dev plan do not support Network Groups. <cc-link href="${addonMigrationScreenUrl}">Migrate to a paid plan</cc-link> to use this feature.`,
  //#endregion
  //#region cc-network-group-member-card
  'cc-network-group-member-card.deleted-member.badge': `Resource deleted`,
  'cc-network-group-member-card.deleted-member.warning': `This resource has been deleted. The member still appears in this Network Group but is no longer linked to a live resource. You can unlink it to clean up the Network Group.`,
  'cc-network-group-member-card.link.dashboard-addon': `Add-on overview`,
  'cc-network-group-member-card.link.dashboard-application': `Application overview`,
  'cc-network-group-member-card.nb-of-peers': /** @param {{ nbOfPeers: number}} _ */ ({ nbOfPeers }) =>
    `${nbOfPeers} ${plural(nbOfPeers, 'peer')}`,
  'cc-network-group-member-card.unlink': `Unlink member`,
  'cc-network-group-member-card.unlink.a11y-name': /** @param {{ label: string }} _ */ ({ label }) =>
    `Unlink member ${label}`,
  //#endregion
  //#region cc-network-group-member-list
  'cc-network-group-member-list.cli.content.get-ng-command': `Get Network Group info:`,
  'cc-network-group-member-list.cli.content.instruction': getCliInstructions,
  'cc-network-group-member-list.cli.content.intro': `You can manage your Network Group members directly from your terminal using the commands below.`,
  'cc-network-group-member-list.cli.content.link-member-command': `Link a member to a Network Group:`,
  'cc-network-group-member-list.cli.content.unlink-member-command': `Unlink a member from a Network Group:`,
  'cc-network-group-member-list.documentation.text': `Network Groups - Documentation`,
  'cc-network-group-member-list.error': `Something went wrong while loading the Network Group info`,
  'cc-network-group-member-list.heading': `Members and Peers`,
  'cc-network-group-member-list.intro': `A member is a resource linked to a Network Group: an application, an add-on, or an external resource. Each member is identified by a dedicated domain name within the private network. A peer is an instance of a member, with its own IP address. All peers in the same Network Group can reach one another on any port, through a secure WireGuard tunnel.`,
  'cc-network-group-member-list.link-form.button': `Link member`,
  'cc-network-group-member-list.link-form.empty': `No resource is available. They may already be linked to this Network Group.`,
  'cc-network-group-member-list.link-form.heading': `Link a member`,
  'cc-network-group-member-list.link-form.select-label': `Resource to link`,
  'cc-network-group-member-list.member-list.empty': `No member is linked to this Network Group`,
  'cc-network-group-member-list.member.link.error': `Something went wrong while linking the member to the Network Group`,
  'cc-network-group-member-list.member.link.success': `The member has been linked to the Network Group successfully`,
  'cc-network-group-member-list.member.logo.a11y-name.external': `External resource`,
  'cc-network-group-member-list.member.unlink.error': `Something went wrong while unlinking the Network Group member`,
  'cc-network-group-member-list.member.unlink.success': `The Network Group member has been unlinked successfully`,
  'cc-network-group-member-list.refresh.error': `The operation succeeded, but the list could not be refreshed. Refresh the page.`,
  'cc-network-group-member-list.unlink.dialog.desc': `Unlinking a member from your Network Group also unlinks all its peers. Are you sure you want to continue?`,
  'cc-network-group-member-list.unlink.dialog.heading': `Confirm unlink`,
  'cc-network-group-member-list.unlink.dialog.unlink-btn': `Confirm and unlink`,
  //#endregion
  //#region cc-network-group-peer-card
  'cc-network-group-peer-card.external-peer.config-link': /** @param {{ label: string }} _ */ ({ label }) =>
    `Download peer configuration for ${label}`,
  'cc-network-group-peer-card.metadata.id': `Identifier`,
  'cc-network-group-peer-card.metadata.ip': `IP address`,
  'cc-network-group-peer-card.metadata.key': `Public key`,
  'cc-network-group-peer-card.metadata.peer-type': `Peer type`,
  'cc-network-group-peer-card.metadata.peer-type.clever-peer': `Clever Peer`,
  'cc-network-group-peer-card.metadata.peer-type.external-peer': `External Peer`,
  //#endregion
  //#region cc-notice
  'cc-notice.close': `Close this notice`,
  'cc-notice.icon-alt.danger': `Error`,
  'cc-notice.icon-alt.info': `Information`,
  'cc-notice.icon-alt.success': `Success`,
  'cc-notice.icon-alt.warning': `Warning`,
  //#endregion
  //#region cc-oauth-consumer-form
  'cc-oauth-consumer-form.auth.title': `Rights`,
  'cc-oauth-consumer-form.create-button': `Create`,
  'cc-oauth-consumer-form.create-title': `New OAuth consumer`,
  'cc-oauth-consumer-form.create.error': `Something went wrong while creating the OAuth consumer`,
  'cc-oauth-consumer-form.create.success': /** @param {{oauthConsumerName: string}} _ */ ({ oauthConsumerName }) =>
    sanitize`The OAuth consumer <strong>${oauthConsumerName}</strong> has been created successfully`,
  'cc-oauth-consumer-form.danger-zone.description': `This action is permanent and cannot be undone. Once the OAuth consumer has been deleted, users will no longer be able to log in to the application via OAuth, and all granted rights will be revoked.`,
  'cc-oauth-consumer-form.danger-zone.title': `Delete OAuth consumer`,
  'cc-oauth-consumer-form.delete-button': `Delete`,
  'cc-oauth-consumer-form.delete.error': `Something went wrong while deleting the OAuth consumer`,
  'cc-oauth-consumer-form.delete.success': `The OAuth consumer has been deleted successfully`,
  'cc-oauth-consumer-form.documentation.text': `OAuth consumer - Documentation`,
  'cc-oauth-consumer-form.info.base-url': `Base URL`,
  'cc-oauth-consumer-form.info.base-url.help': `The base URL to be used for OAuth redirects. For instance: "https://example.com"`,
  'cc-oauth-consumer-form.info.description-input': `Description`,
  'cc-oauth-consumer-form.info.description.help': `It will be displayed to all users of your application`,
  'cc-oauth-consumer-form.info.homepage-url': `Homepage URL`,
  'cc-oauth-consumer-form.info.homepage-url.help': `The full URL to your application homepage. For instance: "https://www.example.com/home"`,
  'cc-oauth-consumer-form.info.image': `Logo URL`,
  'cc-oauth-consumer-form.info.image.help': `This image will be displayed to users when they request authorization, making it easier for them to recognize your service. For instance: "https://example.com"`,
  'cc-oauth-consumer-form.info.name': `Name`,
  'cc-oauth-consumer-form.info.name.help': `A name users will recognize and trust`,
  'cc-oauth-consumer-form.info.placeholder': `No value yet...`,
  'cc-oauth-consumer-form.info.title': `Application details`,
  'cc-oauth-consumer-form.info.url.error': `Enter valid url. Example: "https://example.com"`,
  'cc-oauth-consumer-form.load.error': `Something went wrong while loading OAuth consumer info`,
  'cc-oauth-consumer-form.reset-button': `Reset`,
  'cc-oauth-consumer-form.rights.access-all': `Access all`,
  'cc-oauth-consumer-form.rights.access-organisations': `Organisations`,
  'cc-oauth-consumer-form.rights.access-organisations-bills': `Organisations' bills`,
  'cc-oauth-consumer-form.rights.access-organisations-consumption-statistics': `Organisations' consumption statistics`,
  'cc-oauth-consumer-form.rights.access-organisations-credit-count': `Organisations' credit count`,
  'cc-oauth-consumer-form.rights.access-personal-information': `Personal information`,
  'cc-oauth-consumer-form.rights.description': `The OAuth consumer will prompt for the following rights:`,
  'cc-oauth-consumer-form.rights.error': `Please select at least one option.`,
  'cc-oauth-consumer-form.rights.legend-access': `Access rights`,
  'cc-oauth-consumer-form.rights.legend-manage': `Manage rights`,
  'cc-oauth-consumer-form.rights.manage-all': `Manage all`,
  'cc-oauth-consumer-form.rights.manage-organisations': `Organisations`,
  'cc-oauth-consumer-form.rights.manage-organisations-applications': `Organisations' applications`,
  'cc-oauth-consumer-form.rights.manage-organisations-members': `Organisations' members`,
  'cc-oauth-consumer-form.rights.manage-organisations-services': `Organisations' add-ons`,
  'cc-oauth-consumer-form.rights.manage-personal-information': `Personal information`,
  'cc-oauth-consumer-form.rights.manage-ssh-keys': `SSH keys`,
  'cc-oauth-consumer-form.update-button': `Save`,
  'cc-oauth-consumer-form.update-title': `Edit your OAuth consumer`,
  'cc-oauth-consumer-form.update.error': /** @param {{oauthConsumerName: string}} _ */ ({ oauthConsumerName }) =>
    sanitize`Something went wrong while updating the OAuth consumer <strong>(${oauthConsumerName})</strong>`,
  'cc-oauth-consumer-form.update.success': /** @param {{oauthConsumerName: string}} _ */ ({ oauthConsumerName }) =>
    sanitize`The OAuth consumer <strong>(${oauthConsumerName})</strong> has been updated successfully`,
  //#endregion
  //#region cc-oauth-consumer-info
  'cc-oauth-consumer-info.access.title': `Configuration details`,
  'cc-oauth-consumer-info.error': `Something went wrong while loading OAuth consumer info`,
  'cc-oauth-consumer-info.info.base-url': `Application base URL`,
  'cc-oauth-consumer-info.info.description': `Your application is configured for these URLs and needs the following credentials to use the consumer:`,
  'cc-oauth-consumer-info.info.homepage-url': `Application homepage URL`,
  'cc-oauth-consumer-info.info.key': `Key`,
  'cc-oauth-consumer-info.info.secret': `Secret`,
  'cc-oauth-consumer-info.rights-title.access': `Access rights:`,
  'cc-oauth-consumer-info.rights-title.manage': `Management rights:`,
  'cc-oauth-consumer-info.rights.access-organisations': `Organisations`,
  'cc-oauth-consumer-info.rights.access-organisations-bills': `Organisations' bills`,
  'cc-oauth-consumer-info.rights.access-organisations-consumption-statistics': `Organisations' consumption statistics`,
  'cc-oauth-consumer-info.rights.access-organisations-credit-count': `Organisations' credit count`,
  'cc-oauth-consumer-info.rights.access-personal-information': `Personal information`,
  'cc-oauth-consumer-info.rights.almighty': `Almighty`,
  'cc-oauth-consumer-info.rights.description': `The OAuth consumer will prompt the application users for the following rights:`,
  'cc-oauth-consumer-info.rights.edit': `Edit the information`,
  'cc-oauth-consumer-info.rights.manage-organisations': `Organisations`,
  'cc-oauth-consumer-info.rights.manage-organisations-applications': `Organisations' applications`,
  'cc-oauth-consumer-info.rights.manage-organisations-members': `Organisations' members`,
  'cc-oauth-consumer-info.rights.manage-organisations-services': `Organisations' add-ons`,
  'cc-oauth-consumer-info.rights.manage-personal-information': `Personal information`,
  'cc-oauth-consumer-info.rights.manage-ssh-keys': `SSH keys`,
  'cc-oauth-consumer-info.rights.title': `Rights`,
  //#endregion
  //#region cc-order-summary
  'cc-order-summary.create': `Create`,
  'cc-order-summary.title': `Order summary`,
  //#endregion
  //#region cc-orga-member-card
  'cc-orga-member-card.btn.cancel.accessible-name': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    `Cancel editing member - ${memberIdentity}`,
  'cc-orga-member-card.btn.cancel.visible-text': `Cancel`,
  'cc-orga-member-card.btn.delete.accessible-name': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    `Remove the member - ${memberIdentity}`,
  'cc-orga-member-card.btn.delete.visible-text': `Remove`,
  'cc-orga-member-card.btn.edit.accessible-name': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    `Edit the member - ${memberIdentity}`,
  'cc-orga-member-card.btn.edit.visible-text': `Edit`,
  'cc-orga-member-card.btn.leave.accessible-name': `Leave the organisation`,
  'cc-orga-member-card.btn.leave.visible-text': `Leave`,
  'cc-orga-member-card.btn.validate.accessible-name': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    `Validate the edit of the member - ${memberIdentity}`,
  'cc-orga-member-card.btn.validate.visible-text': `Validate`,
  'cc-orga-member-card.current-user': `Your account`,
  'cc-orga-member-card.error.last-admin.heading': `You are the last admin of the organisation`,
  'cc-orga-member-card.error.last-admin.text': `Add a new admin before you can edit your role or leave the organisation`,
  'cc-orga-member-card.mfa-disabled': `2FA disabled`,
  'cc-orga-member-card.mfa-enabled': `2FA enabled`,
  'cc-orga-member-card.role.accounting': `Accountant`,
  'cc-orga-member-card.role.admin': `Admin`,
  'cc-orga-member-card.role.developer': `Developer`,
  'cc-orga-member-card.role.label': `Role`,
  'cc-orga-member-card.role.manager': `Manager`,
  //#endregion
  //#region cc-orga-member-list
  'cc-orga-member-list.delete.error': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    sanitize`Something went wrong while trying to remove <strong>${memberIdentity}</strong> from this organisation.`,
  'cc-orga-member-list.delete.success': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    sanitize`<strong>${memberIdentity}</strong> has been removed from this organisation.`,
  'cc-orga-member-list.documentation.text': `Organisation members - Documentation`,
  'cc-orga-member-list.edit.error': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    sanitize`Something went wrong while editing <strong>${memberIdentity}</strong>.`,
  'cc-orga-member-list.edit.success': /** @param {{memberIdentity: string}} _ */ ({ memberIdentity }) =>
    sanitize`The role of <strong>${memberIdentity}</strong> has been modified.`,
  'cc-orga-member-list.error': `Something went wrong while loading the organisation member list.`,
  'cc-orga-member-list.error-member-not-found.heading': `Member not found`,
  'cc-orga-member-list.error-member-not-found.text': () =>
    sanitize`<p>The member has left the organisation or has been removed by someone else after loading the list of members.<p><p><strong>Refresh your page</strong> to get the updated list of members.</p>`,
  'cc-orga-member-list.error.unauthorised.heading': `Unauthorised action`,
  'cc-orga-member-list.error.unauthorised.text': `Only admins may invite, edit or remove other admins.`,
  'cc-orga-member-list.filter.mfa': `Accounts not secured with 2FA`,
  'cc-orga-member-list.filter.name': `Filter by name or email address`,
  'cc-orga-member-list.invite.email.error-duplicate': `This user is already a member of this organisation.`,
  'cc-orga-member-list.invite.email.format': `name@example.com`,
  'cc-orga-member-list.invite.email.label': `Email address`,
  'cc-orga-member-list.invite.heading': `Invite a member`,
  'cc-orga-member-list.invite.info': () =>
    sanitize`More information about roles in the <cc-link href="${getDocUrl('/account/organizations/#roles-and-privileges')}">Roles and Organisations</cc-link> page.`,
  'cc-orga-member-list.invite.role.accounting': `Accountant`,
  'cc-orga-member-list.invite.role.admin': `Admin`,
  'cc-orga-member-list.invite.role.developer': `Developer`,
  'cc-orga-member-list.invite.role.label': `Role`,
  'cc-orga-member-list.invite.role.manager': `Manager`,
  'cc-orga-member-list.invite.submit': `Invite`,
  'cc-orga-member-list.invite.submit.error': /** @param {{userEmail: string}} _ */ ({ userEmail }) =>
    sanitize`Something went wrong when trying to invite <strong>${userEmail}</strong>.`,
  'cc-orga-member-list.invite.submit.error-rate-limit.message': `Wait a few minutes before trying again.`,
  'cc-orga-member-list.invite.submit.error-rate-limit.title': `You have tried to invite members too many times`,
  'cc-orga-member-list.invite.submit.success': /** @param {{userEmail: string}} _ */ ({ userEmail }) =>
    sanitize`An email has been sent to invite <strong>${userEmail}</strong> into this organisation.`,
  'cc-orga-member-list.leave.btn': `Leave the organisation`,
  'cc-orga-member-list.leave.error': `Something went wrong when trying to leave the organisation.`,
  'cc-orga-member-list.leave.error-last-admin.heading': `You are the last admin of your organisation`,
  'cc-orga-member-list.leave.error-last-admin.text': `Add a new admin before you can edit your role or leave the organisation`,
  'cc-orga-member-list.leave.heading': `Danger zone`,
  'cc-orga-member-list.leave.success': `You have left the organisation.`,
  'cc-orga-member-list.leave.text': () => sanitize`
    <p>Leaving the organisation is not subject to any confirmation.</p>
    <p>If you change your mind afterwards, you will need to ask someone from this organisation to be invited again.</p>
  `,
  'cc-orga-member-list.list.heading': `Members`,
  'cc-orga-member-list.main-heading': `Manage organisation members`,
  'cc-orga-member-list.no-result': `No results matching your criteria.`,
  //#endregion
  //#region cc-payment-warning
  'cc-payment-warning.billing-page-link': /** @param {{orgaName: string, orgaBillingLink: string}} _ */ ({
    orgaName,
    orgaBillingLink,
  }) =>
    sanitize`<cc-link href="${orgaBillingLink}" a11y-desc="Go to the billing page - ${orgaName}">Go to the billing page</cc-link>`,
  'cc-payment-warning.generic.default-payment-method-is-expired': /** @param {{orgaName: string}} _ */ ({ orgaName }) =>
    sanitize`<strong>${orgaName}</strong> has a default payment method but it has expired.`,
  'cc-payment-warning.generic.no-default-payment-method': /** @param {{orgaName: string}} _ */ ({ orgaName }) =>
    sanitize`<strong>${orgaName}</strong> has registered payment methods but none of them are set as default.`,
  'cc-payment-warning.generic.no-payment-method': /** @param {{orgaName: string}} _ */ ({ orgaName }) =>
    sanitize`<strong>${orgaName}</strong> doesn't have any registered payment method.`,
  'cc-payment-warning.home': /** @param {{orgaCount: number}} _ */ ({ orgaCount }) => {
    const organisation = plural(orgaCount, 'organisation');
    return `To avoid any suspension of your services and deletion of your data, please check the billing info related to the following ${organisation}:`;
  },
  'cc-payment-warning.home.title': `Beware! Something is wrong with your payment methods.`,
  'cc-payment-warning.orga.default-payment-method-is-expired': `To avoid any suspension of your services and deletion of your data, add a valid payment method and set it as default.`,
  'cc-payment-warning.orga.default-payment-method-is-expired.title': `Beware! Your default payment method has expired`,
  'cc-payment-warning.orga.no-default-payment-method': `To avoid any suspension of your services and deletion of your data, set one of them as default.`,
  'cc-payment-warning.orga.no-default-payment-method.title': `Beware! You have registered payment methods but none of them are set as default`,
  'cc-payment-warning.orga.no-payment-method': `To avoid any suspension of your services and deletion of your data, add a valid payment method and set it as default.`,
  'cc-payment-warning.orga.no-payment-method.title': `Beware! You don't have any registered payment method`,
  //#endregion
  //#region cc-picker
  'cc-picker.required': `required`,
  //#endregion
  //#region cc-plan-picker
  'cc-plan-picker.legend': `Select your plan`,
  'cc-plan-picker.legend.customize': `Customize your plan`,
  //#endregion
  //#region cc-pricing-estimation
  'cc-pricing-estimation.count.label': /** @param {{productCount: number}} _ */ ({ productCount }) =>
    plural(productCount, 'product'),
  'cc-pricing-estimation.error': `An error occurred while loading pricing details.`,
  'cc-pricing-estimation.estimated-price-name.1000-minutes': `Price (${formatNumber(lang, 1000)} minutes)`,
  'cc-pricing-estimation.estimated-price-name.30-days': `estimated/30\u00A0days`,
  'cc-pricing-estimation.estimated-price-name.day': `estimated/Day`,
  'cc-pricing-estimation.estimated-price-name.hour': `estimated/Hour`,
  'cc-pricing-estimation.estimated-price-name.minute': `estimated/Minute`,
  'cc-pricing-estimation.estimated-price-name.second': `estimated/Second`,
  'cc-pricing-estimation.feature.connection-limit': `Connection limit: `,
  'cc-pricing-estimation.feature.cpu': `vCPUs: `,
  'cc-pricing-estimation.feature.custom': /** @param {{featureName: string}} _ */ ({ featureName }) =>
    `${featureName}: `,
  'cc-pricing-estimation.feature.databases': `Databases: `,
  'cc-pricing-estimation.feature.dedicated': `Dedicated`,
  'cc-pricing-estimation.feature.disk-size': `Disk size: `,
  'cc-pricing-estimation.feature.gpu': `GPUs: `,
  'cc-pricing-estimation.feature.has-logs': `Logs: `,
  'cc-pricing-estimation.feature.has-metrics': `Metrics: `,
  'cc-pricing-estimation.feature.is-migratable': `Migration tool`,
  'cc-pricing-estimation.feature.max-db-size': `Max DB size: `,
  'cc-pricing-estimation.feature.memory': `RAM: `,
  'cc-pricing-estimation.feature.version': `Version: `,
  'cc-pricing-estimation.heading': `My selection`,
  'cc-pricing-estimation.hide': `Hide`,
  'cc-pricing-estimation.label.currency': `Currency: `,
  'cc-pricing-estimation.label.temporality': `Unit of time: `,
  'cc-pricing-estimation.plan.delete': /** @param {{productName: string, planName: string}} _ */ ({
    productName,
    planName,
  }) => `Remove ${productName} - ${planName}`,
  'cc-pricing-estimation.plan.qty.btn.decrease': /** @param {{productName: string, planName: string}} _ */ ({
    productName,
    planName,
  }) => `Decrease quantity - ${productName} (${planName})`,
  'cc-pricing-estimation.plan.qty.btn.increase': /** @param {{productName: string, planName: string}} _ */ ({
    productName,
    planName,
  }) => `Increase quantity - ${productName} (${planName})`,
  'cc-pricing-estimation.plan.qty.label': `Quantity: `,
  'cc-pricing-estimation.plan.total.label': /** @param {{productName: string, planName: string}} _ */ ({
    productName,
    planName,
  }) => `Total for ${productName} ${planName}`,
  'cc-pricing-estimation.price': /** @param {{price: number, currency: string, digits: number}} _ */ ({
    price,
    currency,
    digits,
  }) => formatCurrency(lang, price, { currency, minimumFractionDigits: digits, maximumFractionDigits: digits }),
  'cc-pricing-estimation.price-name.1000-minutes': `Price (${formatNumber(lang, 1000)} minutes)`,
  'cc-pricing-estimation.price-name.30-days': `Price/30\u00A0days`,
  'cc-pricing-estimation.price-name.day': `Price/Day`,
  'cc-pricing-estimation.price-name.hour': `Price/Hour`,
  'cc-pricing-estimation.price-name.minute': `Price/Minute`,
  'cc-pricing-estimation.price-name.second': `Price/Second`,
  'cc-pricing-estimation.price.unit.label': `Unit price: `,
  'cc-pricing-estimation.show': `Show`,
  'cc-pricing-estimation.tax-excluded': `tax excl.`,
  'cc-pricing-estimation.total.label': `Total: `,
  'cc-pricing-estimation.type.boolean': /** @param {{boolean: boolean}} _ */ ({ boolean }) =>
    `${boolean ? 'Yes' : 'No'}`,
  'cc-pricing-estimation.type.boolean-shared': /** @param {{shared: boolean}} _ */ ({ shared }) =>
    `${shared ? 'Shared' : 'Dedicated'}`,
  'cc-pricing-estimation.type.bytes': /** @param {{bytes: number}} _ */ ({ bytes }) => formatBytes(bytes, 0, 3),
  'cc-pricing-estimation.type.number': /** @param {{number: number}} _ */ ({ number }) => formatNumber(lang, number),
  'cc-pricing-estimation.type.number-cpu-runtime': /** @param {{cpu: number, shared: boolean}} _ */ ({
    cpu,
    shared,
  }) => {
    return shared
      ? sanitize`<em title="Lower priority access to vCPU">${formatNumber(lang, cpu)}<code>*</code></em>`
      : formatNumber(lang, cpu);
  },
  //#endregion
  //#region cc-pricing-header
  'cc-pricing-header.error': `Something went wrong while loading pricing filters.`,
  'cc-pricing-header.label.currency': `Currency`,
  'cc-pricing-header.label.temporality': `Unit of time`,
  'cc-pricing-header.label.zone': `Zone`,
  'cc-pricing-header.price-name.1000-minutes': `Price (${formatNumber(lang, 1000)} minutes)`,
  'cc-pricing-header.price-name.30-days': `Price/30\u00A0days`,
  'cc-pricing-header.price-name.day': `Price/Day`,
  'cc-pricing-header.price-name.hour': `Price/Hour`,
  'cc-pricing-header.price-name.minute': `Price/Minute`,
  'cc-pricing-header.price-name.second': `Price/Second`,
  //#endregion
  //#region cc-pricing-product
  'cc-pricing-product.add-button': /** @param {{productName: string, size: string}} _ */ ({ productName, size }) =>
    `Add ${productName} - ${size} to the cost estimate`,
  'cc-pricing-product.error': `An error occurred while loading pricing details.`,
  'cc-pricing-product.feature.connection-limit': `Connection limit`,
  'cc-pricing-product.feature.cpu': `vCPUs`,
  'cc-pricing-product.feature.databases': `Databases`,
  'cc-pricing-product.feature.dedicated': `Dedicated`,
  'cc-pricing-product.feature.disk-size': `Disk size`,
  'cc-pricing-product.feature.gpu': `GPUs`,
  'cc-pricing-product.feature.has-logs': `Logs`,
  'cc-pricing-product.feature.has-metrics': `Metrics`,
  'cc-pricing-product.feature.is-migratable': `Migration tool`,
  'cc-pricing-product.feature.max-db-size': `Max DB size`,
  'cc-pricing-product.feature.memory': `RAM`,
  'cc-pricing-product.feature.version': `Version`,
  'cc-pricing-product.plan': `Plan`,
  'cc-pricing-product.price': /** @param {{price: number, currency: string, digits: number}} _ */ ({
    price,
    currency,
    digits,
  }) => formatCurrency(lang, price, { currency, minimumFractionDigits: digits, maximumFractionDigits: digits }),
  'cc-pricing-product.price-name.1000-minutes': `Price (${formatNumber(lang, 1000)} minutes)`,
  'cc-pricing-product.price-name.30-days': `Price/30\u00A0days`,
  'cc-pricing-product.price-name.day': `Price/Day`,
  'cc-pricing-product.price-name.hour': `Price/Hour`,
  'cc-pricing-product.price-name.minute': `Price/Minute`,
  'cc-pricing-product.price-name.second': `Price/Second`,
  'cc-pricing-product.type.boolean': /** @param {{boolean: boolean}} _ */ ({ boolean }) => `${boolean ? 'Yes' : 'No'}`,
  'cc-pricing-product.type.boolean-shared': /** @param {{shared: boolean}} _ */ ({ shared }) =>
    `${shared ? 'Shared' : 'Dedicated'}`,
  'cc-pricing-product.type.bytes': /** @param {{bytes: number}} _ */ ({ bytes }) => formatBytes(bytes, 0, 3),
  'cc-pricing-product.type.number': /** @param {{number: number}} _ */ ({ number }) => formatNumber(lang, number),
  'cc-pricing-product.type.number-cpu-runtime': /** @param {{cpu: number, shared: boolean}} _ */ ({ cpu, shared }) => {
    return shared
      ? sanitize`<em title="Lower priority access to vCPU">${formatNumber(lang, cpu)}<code>*</code></em>`
      : formatNumber(lang, cpu);
  },
  //#endregion
  //#region cc-pricing-product-consumption
  'cc-pricing-product-consumption.add': `Add`,
  'cc-pricing-product-consumption.bytes': /** @param {{bytes: number}} _ */ ({ bytes }) => formatBytesSi(bytes),
  'cc-pricing-product-consumption.bytes-unit': /** @param {{bytes: number}} _ */ ({ bytes }) => getUnit(bytes),
  'cc-pricing-product-consumption.cold-storage.label': `storage`,
  'cc-pricing-product-consumption.cold-storage.title': `Cold storage (HDD):`,
  'cc-pricing-product-consumption.error': `An error occurred while retrieving pricing details.`,
  'cc-pricing-product-consumption.hot-storage.label': `storage`,
  'cc-pricing-product-consumption.hot-storage.title': `Hot storage (SSD):`,
  'cc-pricing-product-consumption.inbound-traffic.label': `traffic in`,
  'cc-pricing-product-consumption.inbound-traffic.title': `Inbound traffic:`,
  'cc-pricing-product-consumption.number': /** @param {{number: number}} _ */ ({ number }) =>
    formatNumber(lang, number),
  'cc-pricing-product-consumption.outbound-traffic.label': `traffic out`,
  'cc-pricing-product-consumption.outbound-traffic.title': `Outbound traffic:`,
  'cc-pricing-product-consumption.price': /** @param {{price: number, currency: string}} _ */ ({ price, currency }) =>
    `${formatCurrency(lang, price, { currency })}`,
  'cc-pricing-product-consumption.price-interval.bytes': /** @param {{price: number, currency: string}} _ */ ({
    price,
    currency,
  }) => {
    const priceInterval = formatCurrency(lang, price, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
      currency,
    });
    const priceOneGigabyte = getUnit(1e9);
    return `${priceInterval} / ${priceOneGigabyte} (30 days)`;
  },
  'cc-pricing-product-consumption.price-interval.free': `FREE`,
  'cc-pricing-product-consumption.price-interval.users':
    /** @param {{userCount: number, price: number, currency: string}} _ */ ({ userCount, price, currency }) => {
      const users = plural(userCount, 'user');
      const priceInterval = formatCurrency(lang, price * userCount, { currency });
      return `${priceInterval} / ${userCount} ${users} (30 days)`;
    },
  'cc-pricing-product-consumption.private-users.label': `private users`,
  'cc-pricing-product-consumption.private-users.title': `Private users:`,
  'cc-pricing-product-consumption.public-users.label': `public users`,
  'cc-pricing-product-consumption.public-users.title': `Public users:`,
  'cc-pricing-product-consumption.quantity': `Quantity`,
  'cc-pricing-product-consumption.size': /** @param {{bytes: number}} _ */ ({ bytes }) => `Size (in ${getUnit(bytes)})`,
  'cc-pricing-product-consumption.storage.label': `storage`,
  'cc-pricing-product-consumption.storage.title': `Storage:`,
  'cc-pricing-product-consumption.subtotal.title': `Subtotal (30 days):`,
  'cc-pricing-product-consumption.toggle-btn.label': `Show more details`,
  'cc-pricing-product-consumption.total.title': `Estimated total (30 days):`,
  'cc-pricing-product-consumption.unit': `Unit`,
  //#endregion
  //#region cc-product-card
  'cc-product-card.select': /** @param {{name: string}} _ */ ({ name }) => `${name} - select this product`,
  //#endregion
  //#region cc-product-list
  'cc-product-list.all-label': `All`,
  'cc-product-list.filter-category-legend': `Filter by category`,
  'cc-product-list.search-empty': `No products matching your search criteria were found.`,
  'cc-product-list.search-label': `Search for a product`,
  //#endregion
  //#region cc-range-selector
  'cc-range-selector.custom': `Customize`,
  'cc-range-selector.error.empty': `You must select a value`,
  'cc-range-selector.label.end': `Select an end value`,
  'cc-range-selector.label.single': `Select a value`,
  'cc-range-selector.label.start': `Select a start value`,
  'cc-range-selector.required': `required`,
  'cc-range-selector.summary.disabled': `disabled`,
  'cc-range-selector.summary.introduction': /** @param {string} legend */ (legend) => `Summary for "${legend}":`,
  'cc-range-selector.summary.selected': `selected`,
  'cc-range-selector.summary.unselected': `unselected`,
  //#endregion
  //#region cc-select
  'cc-select.error.empty': `You must select a value`,
  'cc-select.required': `required`,
  //#endregion
  //#region cc-ssh-key-list
  'cc-ssh-key-list.add.btn': `Add key`,
  'cc-ssh-key-list.add.info': () =>
    sanitize`<p>You need to associate an SSH key to your account to deploy via Git. Use this form to do so.</p><p>You can create an SSH key with the following command:</p><code>ssh-keygen -t ed25519 -C "my-email@example.com"</code><p>The generated public key is written in the "*.pub" file.</p>`,
  'cc-ssh-key-list.add.name': `Name`,
  'cc-ssh-key-list.add.public-key': `Public key`,
  'cc-ssh-key-list.add.title': `Add a new key`,
  'cc-ssh-key-list.documentation.text': `SSH keys - Documentation`,
  'cc-ssh-key-list.error.add': /** @param {{name: string}} _ */ ({ name }) =>
    `An error occurred while adding your new personal key "${name}".`,
  'cc-ssh-key-list.error.delete': /** @param {{name: string}} _ */ ({ name }) =>
    `An error occurred while deleting your personal key "${name}".`,
  'cc-ssh-key-list.error.import': /** @param {{name: string}} _ */ ({ name }) =>
    `An error occurred while importing your GitHub key "${name}".`,
  'cc-ssh-key-list.error.loading': `An error occurred while loading your keys.`,
  'cc-ssh-key-list.error.private-key': `Invalid format: did you enter your private key instead of your public key?`,
  'cc-ssh-key-list.error.required.name': `Enter a name for your SSH key`,
  'cc-ssh-key-list.error.required.public-key': `Enter the public key value`,
  'cc-ssh-key-list.github.empty': `There are no SSH keys available for import from your GitHub account.`,
  'cc-ssh-key-list.github.import': `Import`,
  'cc-ssh-key-list.github.import.a11y': /** @param {{name: string}} _ */ ({ name }) =>
    `Import the GitHub SSH key - ${name}`,
  'cc-ssh-key-list.github.info': () =>
    sanitize`<p>These are the SSH keys from your GitHub account. You can import them to associate them to your Clever Cloud account.</p>`,
  'cc-ssh-key-list.github.title': `GitHub keys`,
  'cc-ssh-key-list.github.unlinked': () =>
    sanitize`There is no GitHub account linked to your Clever Cloud account. You may link your accounts from your <cc-link href="./information">profile</cc-link> page.`,
  'cc-ssh-key-list.personal.delete': `Delete`,
  'cc-ssh-key-list.personal.delete.a11y': /** @param {{name: string}} _ */ ({ name }) =>
    `Delete your personal SSH key - ${name}`,
  'cc-ssh-key-list.personal.empty': `There are no SSH keys associated with your account.`,
  'cc-ssh-key-list.personal.info': () =>
    sanitize`<p>These are the SSH keys associated with your account.</p><p>If you want to check whether a key is already associated or not, you can list the fingerprints of your local keys with the following command:</p><code>ssh-add -l -E sha256</code>`,
  'cc-ssh-key-list.personal.title': `Your keys`,
  'cc-ssh-key-list.success.add': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Your key <strong>${name}</strong> has been added successfully.`,
  'cc-ssh-key-list.success.delete': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Your key <strong>${name}</strong> has been deleted successfully.`,
  'cc-ssh-key-list.success.import': /** @param {{name: string}} _ */ ({ name }) =>
    sanitize`Your key <strong>${name}</strong> has been imported successfully.`,
  'cc-ssh-key-list.title': `SSH Keys`,
  //#endregion
  //#region cc-tcp-redirection
  'cc-tcp-redirection.create-button': `Create`,
  'cc-tcp-redirection.delete-button': `Delete`,
  'cc-tcp-redirection.namespace-additionaldescription-cleverapps': () =>
    sanitize`This namespace is used by all <em>cleverapps.io</em> domains (e.g. <em>my-app.cleverapps.io</em>).`,
  'cc-tcp-redirection.namespace-additionaldescription-default': () =>
    sanitize`This namespace is used by all custom domains (e.g. <em>my-app.com</em>).`,
  'cc-tcp-redirection.namespace-private': `This is your private namespace.`,
  'cc-tcp-redirection.redirection-defined': /** @param {{namespace: string, sourcePort: number}} _ */ ({
    namespace,
    sourcePort,
  }) => {
    return sanitize`This application has a redirection from port <code>${sourcePort}</code> to port <code>4040</code> in the <strong>${namespace}</strong> namespace.`;
  },
  'cc-tcp-redirection.redirection-not-defined': /** @param {{namespace: string}} _ */ ({ namespace }) =>
    sanitize`You can create a redirection in the <strong>${namespace}</strong> namespace.`,
  //#endregion
  //#region cc-tcp-redirection-form
  'cc-tcp-redirection-form.cli.content.add-tcp-redirection-command': `Add a TCP redirection:`,
  'cc-tcp-redirection-form.cli.content.add-tcp-redirection-command-default': `Add a TCP redirection (default namespace):`,
  'cc-tcp-redirection-form.cli.content.instruction': getCliInstructions,
  'cc-tcp-redirection-form.cli.content.intro': `
      You can manage TCP redirections directly from your terminal using the commands below.
    `,
  'cc-tcp-redirection-form.cli.content.list-tcp-redirection-command': `List TCP redirections:`,
  'cc-tcp-redirection-form.cli.content.remove-tcp-redirection-command': `Remove a TCP redirection:`,
  'cc-tcp-redirection-form.create.error': /** @param {{namespace: string}} _ */ ({ namespace }) => {
    return sanitize`Something went wrong while creating a TCP redirection in the <strong>${namespace}</strong> namespace.`;
  },
  'cc-tcp-redirection-form.create.success': /** @param {{namespace: string}} _ */ ({ namespace }) => {
    return sanitize`The TCP redirection in the <strong>${namespace}</strong> namespace has been created successfully.`;
  },
  'cc-tcp-redirection-form.delete.error': /** @param {{namespace: string}} _ */ ({ namespace }) => {
    return sanitize`Something went wrong while deleting the TCP redirection in the <strong>${namespace}</strong> namespace.`;
  },
  'cc-tcp-redirection-form.delete.success': /** @param {{namespace: string}} _ */ ({ namespace }) => {
    return sanitize`The TCP redirection in the <strong>${namespace}</strong> namespace has been deleted successfully.`;
  },
  'cc-tcp-redirection-form.description': () => sanitize`
    <p>
      A TCP redirection allows you to route external traffic to the <code>4040</code> port of the application.<br>
      You can create one TCP redirection per application for each namespace you have access to.
    </p>
    <p>
      A namespace is a group of load balancers: either the default public ones, cleverapps.io, or dedicated ones if you are a Clever Cloud Premium customer.
    </p>
  `,
  'cc-tcp-redirection-form.documentation.text': `TCP Redirections - Documentation`,
  'cc-tcp-redirection-form.empty': `You do not have access to any namespaces.`,
  'cc-tcp-redirection-form.error': `Something went wrong while loading TCP redirections.`,
  'cc-tcp-redirection-form.title': `TCP Redirections`,
  //#endregion
  //#region cc-tile-deployments
  'cc-tile-deployments.empty': `No deployments yet.`,
  'cc-tile-deployments.error': `Something went wrong while loading deployments info.`,
  'cc-tile-deployments.error.icon-a11y-name': `Warning`,
  'cc-tile-deployments.state.cancelled': `Cancelled`,
  'cc-tile-deployments.state.failed': `Failed`,
  'cc-tile-deployments.state.started': `Started`,
  'cc-tile-deployments.state.stopped': `Stopped`,
  'cc-tile-deployments.title': `Last deployments`,
  //#endregion
  //#region cc-tile-instances
  'cc-tile-instances.empty': `No instances. Your app is stopped.`,
  'cc-tile-instances.error': `Something went wrong while loading instances.`,
  'cc-tile-instances.error.icon-a11y-name': `Warning`,
  'cc-tile-instances.status.deploying': `Deploying`,
  'cc-tile-instances.status.running': `Running`,
  'cc-tile-instances.title': `Instances`,
  //#endregion
  //#region cc-tile-metrics
  'cc-tile-metrics.a11y.table-header.cpu': `CPU usage over 24h`,
  'cc-tile-metrics.a11y.table-header.mem': `RAM usage over 24h`,
  'cc-tile-metrics.a11y.table-header.timestamp': `Timestamp`,
  'cc-tile-metrics.about-btn': `Show details about this chart`,
  'cc-tile-metrics.close-btn': `Display chart`,
  'cc-tile-metrics.docs.more-metrics': `More metrics: `,
  'cc-tile-metrics.docs.msg': () => sanitize`<p>Server metrics received in the last 24 hours.</p>
    <p>Each bar represents a time window of <strong>1 hour</strong>.</p>
    <p>The percentage shown is the average over the last hour.</p>`,
  'cc-tile-metrics.empty': `No metrics. Application is stopped.`,
  'cc-tile-metrics.error': `Something went wrong while loading metrics.`,
  'cc-tile-metrics.error.icon-a11y-name': `Warning`,
  'cc-tile-metrics.grafana': `Grafana`,
  'cc-tile-metrics.legend.cpu': `CPU usage over 24h`,
  'cc-tile-metrics.legend.mem': `RAM usage over 24h`,
  'cc-tile-metrics.link-to-grafana': `Open Grafana`,
  'cc-tile-metrics.link-to-metrics': `Open Metrics`,
  'cc-tile-metrics.metrics-link': `Metrics`,
  'cc-tile-metrics.percent': /** @param {{percent: number}} _ */ ({ percent }) => formatPercent(lang, percent),
  'cc-tile-metrics.timestamp-format': /** @param {{timestamp: string|number}} _ */ ({ timestamp }) =>
    formatDate(timestamp),
  'cc-tile-metrics.title': `Server metrics`,
  //#endregion
  //#region cc-tile-requests
  'cc-tile-requests.about-btn': `About this chart...`,
  'cc-tile-requests.close-btn': `Display chart`,
  'cc-tile-requests.date-hours': /** @param {{date: string|number}} _ */ ({ date }) => formatHours(date),
  'cc-tile-requests.date-tooltip': /** @param {{from: string|number, to: string|number}} _ */ ({ from, to }) => {
    const date = formatDateOnly(from);
    const fromH = formatHours(from);
    const toH = formatHours(to);
    return `${date}: from ${fromH} to ${toH}`;
  },
  'cc-tile-requests.docs.msg': /** @param {{windowHours: number}} _ */ ({ windowHours }) => {
    const hour = plural(windowHours, 'hour');
    return sanitize`HTTP requests received in the last 24 hours. Each bar represents a time window of <strong>${windowHours} ${hour}</strong>.`;
  },
  'cc-tile-requests.empty': `No data to display for now.`,
  'cc-tile-requests.error': `Something went wrong while loading HTTP requests.`,
  'cc-tile-requests.error.icon-a11y-name': `Warning`,
  'cc-tile-requests.requests-count': /** @param {{requestCount: number}} _ */ ({ requestCount }) =>
    formatNumberUnit(requestCount),
  'cc-tile-requests.requests-nb': /** @param {{value: number, windowHours: number}} _ */ ({ value, windowHours }) => {
    const request = plural(value, 'request');
    const hour = plural(windowHours, 'hour');
    const formattedValue = formatNumber(lang, value);
    return `${formattedValue} ${request} (in ${windowHours} ${hour})`;
  },
  'cc-tile-requests.requests-nb.total': /** @param {{totalRequests: number}} _ */ ({ totalRequests }) => {
    const request = plural(totalRequests, 'request');
    const formattedValue = formatNumberUnit(totalRequests);
    return `${formattedValue} ${request} in 24 hours`;
  },
  'cc-tile-requests.title': `HTTP requests`,
  //#endregion
  //#region cc-tile-scalability
  'cc-tile-scalability.error': `Something went wrong while loading scalability config.`,
  'cc-tile-scalability.error.icon-a11y-name': `Warning`,
  'cc-tile-scalability.flavor-info': /** @param {Flavor} flavor */ (flavor) => formatFlavor(flavor),
  'cc-tile-scalability.number': `Number`,
  'cc-tile-scalability.size': `Size`,
  'cc-tile-scalability.title': `Scalability`,
  //#endregion
  //#region cc-tile-status-codes
  'cc-tile-status-codes.about-btn': `About this chart...`,
  'cc-tile-status-codes.close-btn': `Display chart`,
  'cc-tile-status-codes.docs.link': () =>
    sanitize`<cc-link href="https://developer.mozilla.org/en/docs/Web/HTTP/Status">HTTP response status codes (MDN)</cc-link>`,
  'cc-tile-status-codes.docs.msg': `Repartition of HTTP response codes returned in the last 24 hours. Click on legend items to show/hide HTTP code categories.`,
  'cc-tile-status-codes.empty': `No data to display for now.`,
  'cc-tile-status-codes.error': `Something went wrong while loading HTTP response codes.`,
  'cc-tile-status-codes.error.icon-a11y-name': `Warning`,
  'cc-tile-status-codes.title': `HTTP response codes`,
  'cc-tile-status-codes.tooltip': /** @param {{value: number, percent: number}} _ */ ({ value, percent }) => {
    const request = plural(value, 'request');
    const formattedValue = formatNumber(lang, value);
    return `${formattedValue} ${request} (${formatPercent(lang, percent)})`;
  },
  //#endregion
  //#region cc-toast
  'cc-toast.close': `Close this notification`,
  'cc-toast.icon-alt.danger': `Error`,
  'cc-toast.icon-alt.info': `Information`,
  'cc-toast.icon-alt.success': `Success`,
  'cc-toast.icon-alt.warning': `Warning`,
  //#endregion
  //#region cc-token-api-creation-form
  'cc-token-api-creation-form.cli.content.create-token': `Create a token:`,
  'cc-token-api-creation-form.cli.content.instruction': getCliInstructions,
  'cc-token-api-creation-form.cli.content.intro': `
      Manage your API tokens from a terminal using the commands below.
    `,
  'cc-token-api-creation-form.cli.content.list-token': `List tokens:`,
  'cc-token-api-creation-form.cli.content.revoke-token': `Revoke a token:`,
  'cc-token-api-creation-form.cli.content.use-token': `Use your API token:`,
  'cc-token-api-creation-form.configuration-step.form.desc.label': `Description`,
  'cc-token-api-creation-form.configuration-step.form.expiration-date.error.invalid':
    /** @param {{ date: string }} _ */ ({ date }) => sanitize`Enter a valid date and time.<br>For instance: ${date}`,
  'cc-token-api-creation-form.configuration-step.form.expiration-date.error.range-overflow':
    /** @param {{ date: string }} _ */ ({ date }) =>
      sanitize`The expiration date must be less than 1 year from now<br>For instance: ${date}`,
  'cc-token-api-creation-form.configuration-step.form.expiration-date.error.range-underflow':
    /** @param {{ date: string }} _ */ ({ date }) =>
      sanitize`The expiration date must be at least 15 minutes from now<br>For instance: ${date}`,
  'cc-token-api-creation-form.configuration-step.form.expiration-date.help.min-max': `Must be at least 15 minutes and up to 1 year from now`,
  'cc-token-api-creation-form.configuration-step.form.expiration-date.label': `Expiration date`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.help.custom': `Specify the expiration date using the following field`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.label': `Time before expiration`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.option-label.custom': `Custom`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.option-label.ninety-days': `90 days`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.option-label.one-year': `1 year`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.option-label.seven-days': `7 days`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.option-label.sixty-days': `60 days`,
  'cc-token-api-creation-form.configuration-step.form.expiration-duration.option-label.thirty-days': `30 days`,
  'cc-token-api-creation-form.configuration-step.form.link.back-to-list': `Back to the API token list`,
  'cc-token-api-creation-form.configuration-step.form.name.label': `Name`,
  'cc-token-api-creation-form.configuration-step.form.submit-button.label': `Continue`,
  'cc-token-api-creation-form.configuration-step.main-heading': `Create a new API token`,
  'cc-token-api-creation-form.configuration-step.nav.label': `Configuration`,
  'cc-token-api-creation-form.copy-step.form.token.label': `Your API token`,
  'cc-token-api-creation-form.copy-step.link.back-to-list': `Go to the token list`,
  'cc-token-api-creation-form.copy-step.main-heading': `Your API token is now created`,
  'cc-token-api-creation-form.copy-step.nav.label': `Get API token`,
  'cc-token-api-creation-form.copy-step.notice.message': `For security reasons, this API token will only be shown once. Make sure to copy and store it in a secure place. After creation, you will not be able to retrieve it ever again. If you lose this token, you will have to revoke it and create a new one.`,
  'cc-token-api-creation-form.error': `Something went wrong while loading information about your account`,
  'cc-token-api-creation-form.link.doc': `API tokens - Documentation`,
  'cc-token-api-creation-form.nav.aria-label': `API token creation steps`,
  'cc-token-api-creation-form.validation-step.error.generic': `Something went wrong while creating the API token`,
  'cc-token-api-creation-form.validation-step.form.link.back-to-configuration': `Back to the configuration step`,
  'cc-token-api-creation-form.validation-step.form.mfa-code.error': `Invalid 2FA code`,
  'cc-token-api-creation-form.validation-step.form.mfa-code.label': `Two-factor authentication code (2FA)`,
  'cc-token-api-creation-form.validation-step.form.password.error': `Invalid password`,
  'cc-token-api-creation-form.validation-step.form.password.label': `Password`,
  'cc-token-api-creation-form.validation-step.form.submit-button.label': `Create`,
  'cc-token-api-creation-form.validation-step.main-heading': `Confirm your identity`,
  'cc-token-api-creation-form.validation-step.nav.label': `Authentication`,
  //#endregion
  //#region cc-token-api-list
  'cc-token-api-list.card.expired': `Expired`,
  'cc-token-api-list.card.expires-soon': `Expires soon`,
  'cc-token-api-list.card.human-friendly-date': /** @param {{ date: Date }} _ */ ({ date }) => formatDatetime(date),
  'cc-token-api-list.card.label.creation': `Creation: `,
  'cc-token-api-list.card.label.expiration': `Expiration: `,
  'cc-token-api-list.card.token-id-icon.a11y-name': `API token identifier`,
  'cc-token-api-list.cli.content.create-token': `Create a token:`,
  'cc-token-api-list.cli.content.instruction': getCliInstructions,
  'cc-token-api-list.cli.content.intro': `
      Manage your API tokens from a terminal using the commands below.
    `,
  'cc-token-api-list.cli.content.list-token': `List tokens:`,
  'cc-token-api-list.cli.content.revoke-token': `Revoke a token:`,
  'cc-token-api-list.cli.content.use-token': `Use your API token:`,
  'cc-token-api-list.create-token': `Create new token`,
  'cc-token-api-list.delete-token': /** @param {{ name: string}} _ */ ({ name }) => `Delete API token - ${name}`,
  'cc-token-api-list.empty': `You haven't created any API tokens yet, or none of them are active. Let's create a new one:`,
  'cc-token-api-list.error': `Something went wrong while loading API tokens`,
  'cc-token-api-list.intro': () =>
    sanitize`Below is the list of <cc-link href="${getDevHubUrl('/api/howto/#request-the-api')}" a11y-desc="API Tokens - Documentation">API tokens</cc-link> linked to your account and their associated information. You may revoke them as needed.`,
  'cc-token-api-list.link.doc': `API tokens - Documentation`,
  'cc-token-api-list.main-heading': `API tokens`,
  'cc-token-api-list.no-password.create-password-btn': `Add a password`,
  'cc-token-api-list.no-password.message': () =>
    sanitize`Your Clever Cloud account is linked via GitHub and has no password. <strong>Setting one is required to create API tokens</strong>. Click the following button to <strong>add a password to your account</strong>, we’ll  send you an email to confirm your identity.`,
  'cc-token-api-list.no-password.reset-password-error': `Something went wrong while trying to send your password creation request`,
  'cc-token-api-list.no-password.reset-password-successful': /** @param {{ email: string }} _ */ ({ email }) =>
    `The email has been sent to ${email}, reload this page once the password is added to your account`,
  'cc-token-api-list.revoke-token': /** @param {{ name: string}} _ */ ({ name }) => `Revoke API token - ${name}`,
  'cc-token-api-list.revoke-token.error': `Something went wrong while revoking the API token`,
  'cc-token-api-list.revoke-token.success': `The API token has been revoked successfully`,
  'cc-token-api-list.update-token': `Modify`,
  'cc-token-api-list.update-token-with-name': /** @param {{ name: string}} _ */ ({ name }) =>
    `Modify API token - ${name}`,
  //#endregion
  //#region cc-token-api-update-form
  'cc-token-api-update-form.back-to-list': `Back to the API token list`,
  'cc-token-api-update-form.cli.content.create-token': `Create a token:`,
  'cc-token-api-update-form.cli.content.instruction': getCliInstructions,
  'cc-token-api-update-form.cli.content.intro': `
    Manage your API tokens from a terminal using the commands below.
  `,
  'cc-token-api-update-form.cli.content.list-token': `List tokens:`,
  'cc-token-api-update-form.cli.content.revoke-token': `Revoke a token:`,
  'cc-token-api-update-form.cli.content.use-token': `Use your API token:`,
  'cc-token-api-update-form.description.label': `Description`,
  'cc-token-api-update-form.error': `Something went wrong while loading API token information`,
  'cc-token-api-update-form.link.doc': `API tokens - Documentation`,
  'cc-token-api-update-form.main-heading': `Modify your API token`,
  'cc-token-api-update-form.name.label': `Name`,
  'cc-token-api-update-form.submit-button': `Modify`,
  'cc-token-api-update-form.update-token.error': `Something went wrong while updating the API token information`,
  'cc-token-api-update-form.update-token.success': `The API token information has been updated successfully`,
  //#endregion
  //#region cc-token-oauth-list
  'cc-token-oauth-list.card.expires-soon': `Expires soon`,
  'cc-token-oauth-list.card.human-friendly-date': /** @param {{ date: Date }} _ */ ({ date }) => formatDatetime(date),
  'cc-token-oauth-list.card.label.creation': `Creation: `,
  'cc-token-oauth-list.card.label.expiration': `Expiration: `,
  'cc-token-oauth-list.card.label.last-used': `Last used: `,
  'cc-token-oauth-list.empty': `You do not have any third-party applications linked to your account`,
  'cc-token-oauth-list.error': `Something went wrong while loading OAuth tokens`,
  'cc-token-oauth-list.intro': () =>
    sanitize`Below is the list of third-party applications linked to your account and their associated information. You may revoke these <cc-link href="${getDevHubUrl('/developers/api/howto/#oauth1')}" a11y-desc="OAuth tokens - Documentation">OAuth tokens</cc-link> as needed.`,
  'cc-token-oauth-list.link.doc': `OAuth tokens - Documentation`,
  'cc-token-oauth-list.main-heading': `OAuth tokens`,
  'cc-token-oauth-list.revoke-all-tokens': `Revoke all OAuth tokens`,
  'cc-token-oauth-list.revoke-all-tokens.error': () =>
    sanitize`Something went wrong while revoking all OAuth tokens.<br>None of your OAuth tokens have been revoked`,
  'cc-token-oauth-list.revoke-all-tokens.partial-error': () =>
    sanitize`Something went wrong while revoking all OAuth tokens.<br>Only some OAuth tokens have been revoked successfully`,
  'cc-token-oauth-list.revoke-all-tokens.success': `All OAuth tokens have been revoked successfully`,
  'cc-token-oauth-list.revoke-token': /** @param {{ consumerName: string}} _ */ ({ consumerName }) =>
    `Revoke OAuth token for ${consumerName}`,
  'cc-token-oauth-list.revoke-token.error': `Something went wrong while revoking the OAuth token`,
  'cc-token-oauth-list.revoke-token.success': `The OAuth token has been revoked successfully`,
  //#endregion
  //#region cc-token-session-list
  'cc-token-session-list.card.clever-team': `Clever Cloud Team`,
  'cc-token-session-list.card.current-session': `Current session`,
  'cc-token-session-list.card.expires-soon': `Expires soon`,
  'cc-token-session-list.card.human-friendly-date': /** @param {{ date: Date }} _ */ ({ date }) => formatDatetime(date),
  'cc-token-session-list.card.label.creation': `Creation: `,
  'cc-token-session-list.card.label.expiration': `Expiration: `,
  'cc-token-session-list.card.label.last-used': `Last used: `,
  'cc-token-session-list.error': `Something went wrong while loading sessions`,
  'cc-token-session-list.intro': `Below is the list of registered sessions for your account, which you can revoke (except the current session):`,
  'cc-token-session-list.main-heading': `Console sessions`,
  'cc-token-session-list.revoke-all-sessions': `Revoke all sessions`,
  'cc-token-session-list.revoke-all-sessions.error': () =>
    sanitize`Something went wrong while revoking all sessions.<br>None of your sessions have been revoked`,
  'cc-token-session-list.revoke-all-sessions.partial-error': () =>
    sanitize`Something went wrong while revoking all sessions.<br>Only some sessions have been revoked successfully`,
  'cc-token-session-list.revoke-all-sessions.success': `All sessions have been revoked successfully`,
  'cc-token-session-list.revoke-session': /** @param {{ tokenNumber: number}} _ */ ({ tokenNumber }) =>
    `Revoke session ${tokenNumber}`,
  'cc-token-session-list.revoke-session.error': `Something went wrong while revoking the session`,
  'cc-token-session-list.revoke-session.success': `The session has been revoked successfully`,
  //#endregion
  //#region cc-zone
  'cc-zone.country': /** @param {{code: string, name: string}} _ */ ({ code, name }) =>
    getCountryName(lang, code, name),
  //#endregion
  //#region cc-zone-input
  'cc-zone-input.error': `Something went wrong while loading zones.`,
  'cc-zone-input.private-map-warning': `Private zones don't appear on the map.`,
  //#endregion
  //#region cc-zone-picker
  'cc-zone-picker.alt.country-name': /** @param {{code: string, name: string}} _ */ ({ code, name }) =>
    getCountryName(lang, code, name),
  'cc-zone-picker.legend': `Select your zone`,
  //#endregion
};
